import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getEffectiveAssociateId } from '../utils/associate';
import { deriveSiteTasks, resolveProjectName } from '../utils/siteTasks';
import type { SiteTask } from '../utils/siteTasks';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import { useOdometerContext } from '../context/OdometerContext';
import { useManualVisit } from '../context/ManualVisitContext';
import { useVisitClock } from '../hooks/useVisitClock';
import { useStoreVisit } from '../hooks/useStoreVisit';
import { useGeolocation, computeVerificationState } from '../hooks/useGeolocation';
import type { GeolocationPosition, VerificationCode } from '../hooks/useGeolocation';
import { formatDateToLocal } from '../utils/timesheetRules';
import { cacheStoreDetailSnapshot, getCachedStoreDetailSnapshot } from '../utils/scheduleCache';
import { deriveNavFromSearchParams } from '../utils/storeDetailNav';
import { arrivalOffer } from '../utils/arrivalGate';
import { emit as emitEvent } from '../utils/eventLog';
import { useVisitTask as useMultiTaskVisit } from '../hooks/useVisitTask';
import { useSplitTimeEnabled } from '../hooks/useSplitTimeEnabled';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { storesApi, callLogsApi, ordersApi, storeTasksApi } from '../services/api';
import { workflowStepsForTask, findWorkflowFlagsForTask, requiredAttachmentsForTask, signatureRequiredForTask, sharedDeliverablesRequiredForTask, teamConfigForTask } from '../utils/taskWorkflow';
import { evaluateTaskCompletion } from '../utils/taskCompletion';
import { evaluateSurveyGate } from '../utils/surveyGate';
import type { Store as ApiStore, PendingTask, CompletedTask, CallLogCreate, CallLog } from '../services/api';
import ManualCallEntry from '../components/ManualCallEntry';
import SurveyQuestions, { SurveyQuestion } from '../components/SurveyQuestions';
import { loadProjectSurvey } from '../utils/surveyLoad';
import PhotoUpload, { UploadedPhoto } from '../components/PhotoUpload';
import DocumentUpload, { UploadedDocument } from '../components/DocumentUpload';
// SignatureCapture is now only used inside CheckoutSummary
import MapDirections from '../components/MapDirections';
import ApiStatusIndicator from '../components/ApiStatusIndicator';
import GPSCheckIn from '../components/GPSCheckIn';
import CheckoutSummary, { CheckoutData, ProjectSummary, completedTasksToProjectSummaries } from '../components/CheckoutSummary';
import { getVisitOrderReview, type VisitOrderReview } from '../utils/visitOrderSummary';
import { parseFlexibleTime as parseTimeToDate, isFullDateTime } from '../utils/visitTime';
import StoreNotes, { StoreNote } from '../components/StoreNotes';
import OdometerEntry from '../components/OdometerEntry';
import RetroactiveStartDay, { RetroactiveStartData } from '../components/RetroactiveStartDay';
import TaskSelector from '../components/TaskSelector';
import { startableSets, startableCalls } from '../utils/siteTasks';
import TaskCompletionPrompt from '../components/TaskCompletionPrompt';
import LocationPermission from '../components/LocationPermission';
import DriveConfirmation, { DriveConfirmationData } from '../components/DriveConfirmation';
import TimeConflictEditor, { parseConflictFromError, ConflictingEntry } from '../components/TimeConflictEditor';
import GpsVerificationReasonModal from '../components/GpsVerificationReasonModal';
import { decideArrivalAction } from '../utils/storeArrival';
import { sanitizeDriveData } from '../utils/driveSegment';

// Visit phases for multi-task workflow
type VisitPhase =
  | 'pending'        // Not checked in yet
  | 'drive_confirm'  // Arrived at store, confirming drive details
  | 'task_select'    // Checked in, choosing task
  | 'task_active'    // Working on a task
  | 'task_complete'  // Task done, prompting for next
  | 'checkout';      // All tasks done, getting signature

interface Project {
  id: number;
  name: string;
  completed: boolean;
}

interface StoreData {
  id: number;
  legacyStoreId: number;  // Legacy store ID used by products API
  retailerId: number | null;  // Site key is legacyStoreId + retailerId (#1944/#1995)
  name: string;
  number: string;
  address: string;
  city: string;
  phone: string;
  lat: number;
  lng: number;
  status: 'pending' | 'in_progress' | 'completed';
  checkInTime: string | null;
  checkOutTime: string | null;
  projects: Project[];
  callLogId: number | null;
  hasOpenOrders: boolean;
  // Project ID passed from Set/Route navigation
  projectIdFromNav: number | null;
  // Distributor ID passed from Set/Route navigation
  distributorIdFromNav: number | null;
  // Task info for direct task start from Sets/Routes page
  taskIdFromNav: number | null;  // setId for sets, routeId for calls
  taskTypeFromNav: 'set' | 'call' | null;
  projectDescFromNav: string | null;
  clientNameFromNav: string | null;
  // Team verdict from the SET handoff (#3629) — null when the sender didn't say.
  teamProjectFromNav: boolean | null;
  teamLeadFromNav: boolean | null;
  // CallLog ID if already created at check-in (from SetDetail navigation)
  callLogIdFromNav: number | null;
  // Visit localId (uuid) handed off by a queue-only check-in (#1987 Phase 3).
  // StoreDetail adopts this exact visit_task_sessions row as the active task
  // so the active id matches the sync queue's id. Null for legacy/server-id
  // nav (e.g. manual-entry or sets paths that still carry a callLogId).
  visitLocalIdFromNav: string | null;
}

/** Null out the nav adoption fields so only SERVER data is persisted (#2066). */
function serverSnapshot(s: StoreData): StoreData {
  return {
    ...s,
    projectIdFromNav: null,
    distributorIdFromNav: null,
    taskIdFromNav: null,
    taskTypeFromNav: null,
    projectDescFromNav: null,
    clientNameFromNav: null,
    callLogIdFromNav: null,
    visitLocalIdFromNav: null,
    teamProjectFromNav: null,
    teamLeadFromNav: null,
  };
}

/**
 * Parse a time string to a Date object (today's date with the given time).
 * Handles multiple input formats:
 * - ISO date strings (e.g., "2026-02-02T21:04:37.123Z")
 * - Formatted time strings (e.g., "4:01 PM", "04:01 PM")
 * - HH:MM:SS format (e.g., "21:04:37")
 * Returns null if parsing fails.
 */
/**
 * Safely format a time value for display.
 * Handles multiple input formats:
 * - ISO date strings (e.g., "2026-02-02T21:04:37.123Z")
 * - Already formatted time strings (e.g., "4:01 PM", "04:01 PM")
 * - HH:MM:SS format (e.g., "21:04:37")
 * - null/undefined
 */
function formatTimeForDisplay(time: string | null | undefined): string {
  if (!time) return '';

  // If it already looks like a formatted time (contains AM/PM), return as-is
  if (/\d{1,2}:\d{2}\s*(AM|PM)/i.test(time)) {
    return time;
  }

  const date = parseTimeToDate(time);
  if (date) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // Fallback: return as-is
  return time;
}

// Collapsible preview of pending tasks - shows what else is available while working on current task
function PendingTasksPreview({
  availableTasks,
  currentTaskId,
  completedTaskIds,
}: {
  availableTasks: { sets: PendingTask[]; calls: PendingTask[] };
  currentTaskId: number;
  completedTaskIds: number[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Filter out current task, completed tasks, and depleted calls — the ONE
  // shared rule (#2125), so this count always agrees with the banner count
  // and the task picker.
  const excludeIds = [...completedTaskIds, currentTaskId];
  const remainingSets = startableSets(availableTasks.sets, excludeIds);
  const remainingCalls = startableCalls(availableTasks.calls, excludeIds);
  const totalRemaining = remainingSets.length + remainingCalls.length;

  if (totalRemaining === 0) return null;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
            <span className="text-xs font-bold text-amber-700">{totalRemaining}</span>
          </div>
          <span className="text-sm font-medium text-slate-700">
            Other task{totalRemaining !== 1 ? 's' : ''} at this store
          </span>
        </div>
        <svg
          className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-100 px-4 pb-4">
          <p className="mb-3 text-xs text-slate-500 italic">
            Finish your current task first, then you can start these
          </p>
          <div className="space-y-2">
            {remainingSets.map((task) => (
              <div
                key={`preview-set-${task.task_id}`}
                className="flex items-center gap-2 rounded-lg bg-purple-50 px-3 py-2"
              >
                <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                  Set
                </span>
                <span className="text-sm text-slate-700 truncate">
                  <span className="font-mono text-primary-600">#{task.project_id}</span> {task.project_description}
                </span>
                {task.set_hours ? (
                  <span className="ml-auto text-xs text-slate-500">{task.set_hours}h</span>
                ) : null}
              </div>
            ))}
            {remainingCalls.map((task) => (
              <div
                key={`preview-call-${task.task_id}`}
                className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2"
              >
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                  Call
                </span>
                <span className="text-sm text-slate-700 truncate">
                  <span className="font-mono text-primary-600">#{task.project_id}</span> {task.project_description}
                </span>
                {/* != null guard (#2125): `0 && ...` renders a literal "0"
                    in JSX — the stray digit after the project name in
                    diag #52's screenshot. */}
                {task.visits_remaining != null && task.frequency != null ? (
                  <span className="ml-auto text-xs text-slate-500">
                    {task.visits_remaining}/{task.frequency} left
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Timer({ startTime }: { startTime: string }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = new Date(startTime).getTime();
    const updateElapsed = () => {
      const now = Date.now();
      setElapsed(Math.floor((now - start) / 1000));
    };
    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  return (
    <span className="font-mono text-2xl font-bold">
      {hours.toString().padStart(2, '0')}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
    </span>
  );
}

// Stale-while-revalidate cache for the resolved store, keyed by storeId:projectId.
// Returning from OrderEntry (or post-check-in nav) remounts this page; hydrating
// `store` synchronously from here lets it paint instantly while the fresh data
// loads in the background, instead of a full-screen "Loading store details…"
// spinner on every return. Module scope = survives route nav within the session.
const storeCache = new Map<string, StoreData>();

export default function StoreDetail() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const storeCacheKey = `${storeId ?? ''}:${searchParams.get('projectId') ?? ''}`;
  // autoCheckinTriggered ref removed - auto check-in feature disabled for GPS safety
  const [store, setStore] = useState<StoreData | null>(() => storeCache.get(storeCacheKey) ?? null);
  // #2066: gate task adoption on SERVER-confirmed status. A same-session
  // (in-memory cache) return starts true so it adopts instantly as before; a
  // cold-load IDB hydrate starts false so adoption waits for fetchStoreData to
  // confirm the status, so a stale cached 'in_progress' can't spawn a phantom task.
  const [serverConfirmed, setServerConfirmed] = useState<boolean>(() => storeCache.has(storeCacheKey));
  const [storeNotes, setStoreNotes] = useState<StoreNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  // Edit visit modal - uses ManualCallEntry component
  const [showEditVisitModal, setShowEditVisitModal] = useState(false);
  const [editingCallLog, setEditingCallLog] = useState<CallLog | null>(null);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  // surveyResponses / photoUploads / documentUploads now come from useVisitTask
  // above and persist to IndexedDB (#1471 Phase 5). The pre-#1471 sessionStorage
  // restore logic has been removed — IDB covers it.
  const [surveyQuestions, setSurveyQuestions] = useState<SurveyQuestion[]>([]);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);
  const [surveyId, setSurveyId] = useState<number | null>(null);
  // #2088: survey fetch failure is a surfaced, retryable state — never demo data.
  const [surveyError, setSurveyError] = useState(false);
  const [surveyRetryNonce, setSurveyRetryNonce] = useState(0);
  const surveyFetchedFor = useRef<number | null>(null);
  const surveyDesiredProject = useRef<number | null>(null);
  const [showOptionalUploads, setShowOptionalUploads] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showCheckoutSummary, setShowCheckoutSummary] = useState(false);
  // #2340 — orders/OOS/on-hand captured this visit, loaded when checkout opens so
  // the manager can review the work before signing.
  const [orderReview, setOrderReview] = useState<VisitOrderReview | null>(null);
  const [showPauseVisitModal, setShowPauseVisitModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // #2340 — when the checkout summary opens, load this visit's captured orders /
  // OOS / on-hand so the manager can review them before signing. Read-only; never
  // blocks checkout (the reader returns an empty summary on any failure).
  useEffect(() => {
    if (!showCheckoutSummary || !store) {
      setOrderReview(null);
      return;
    }
    let cancelled = false;
    getVisitOrderReview(store.visitLocalIdFromNav, store.id).then((review) => {
      if (!cancelled) setOrderReview(review);
    });
    return () => {
      cancelled = true;
    };
  }, [showCheckoutSummary, store]);
  const [taskError, setTaskError] = useState<string | null>(null);
  // Tracks the in-flight "Finish This Task" submission so the button can
  // disable + show a spinner. Without this, reps double-tap during the
  // survey/upload phase and create unintended state churn (#1946).
  const [isFinishing, setIsFinishing] = useState(false);
  const [pendingGpsOverrideReason, setPendingGpsOverrideReason] = useState<string | undefined>(undefined);

  // Time conflict state for manual entries
  const [timeConflicts, setTimeConflicts] = useState<ConflictingEntry[]>([]);
  const [showTimeConflictEditor, setShowTimeConflictEditor] = useState(false);
  const [conflictTimeIn, setConflictTimeIn] = useState('');
  const [conflictTimeOut, setConflictTimeOut] = useState('');

  // GPS verification state for manual entries (#1779)
  const { getCurrentPosition: getVerificationPosition } = useGeolocation();
  const [pendingManualEntry, setPendingManualEntry] = useState<{
    baseCallLogData: CallLogCreate;
    position: GeolocationPosition | null;
    code: VerificationCode;
    distance: number | null;
  } | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const {
    dayData,
    checkIntoStore,
    checkOutOfStore,
    cancelStoreVisit,
    startDay,
    injectManualVisit,
    isLoading: isDayLoading,
    setCurrentEntryCallLogId,
    markCallLogsVoided,
  } = useDayTrackingContext();

  const {
    odometerData,
    isLoading: isOdometerLoading,
    recordStoreArrival,
    recordStoreDeparture,
    calculateExpectedOdometer,
    startDayOdometer,
  } = useOdometerContext();

  const { isOnline } = useConnectionStatus();

  const [showOdometerEntry, setShowOdometerEntry] = useState(false);
  const [calculatedOdometer, setCalculatedOdometer] = useState<number | null>(null);

  // Start Day flow state (for when user hasn't started their day yet)
  const [showStartDayFlow, setShowStartDayFlow] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_hasLocationPermission, setHasLocationPermission] = useState<boolean | null>(null);
  // Retroactive start day - stores drive data to pass to first check-in
  // Using the DriveToStoreData type from useStoreVisit
  const [retroactiveDriveData, setRetroactiveDriveData] = useState<{
    startTime: Date;
    endTime: Date;
    durationMinutes: number;
    startOdometer?: number;
    endOdometer?: number;
  } | null>(null);

  const {
    checkIn: apiCheckIn,
  } = useStoreVisit();

  // Multi-task visit state (IDB-backed via useVisitTask — #1471)
  const {
    visitState: multiTaskState,
    isHydrated: isVisitHydrated,
    startVisit: startMultiTaskVisit,
    setDriveData,  // Will be used with DriveConfirmation component
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setGPSData: _setGPSData,  // GPS data passed during startVisit instead
    startTask,
    reconcileCurrentTaskWorkflow,
    pauseCurrentTask,
    resumePausedTask,
    discardPausedTask,
    finishCurrentTask,
    abandonCurrentTask,
    getCompletedTaskIds,
    getTotalTimeMinutes,
    getCurrentTaskLocalId,
    submitAllTasks,
    resetVisit: resetMultiTaskVisit,
    // Per-task persisted state (survey + photo + document queue)
    surveyResponses,
    saveSurveyResponse,
    photoUploads,
    documentUploads,
    addPhoto,
    addDocument,
    removePhoto,
    removeDocument,
    recoverFileBlobUrl,
  } = useMultiTaskVisit();

  // #2104 Split Time feature gate (django-flags, default OFF). Gates the editable
  // checkout time + Split Time control; off = checkout shows only tracked time.
  const splitTimeEnabled = useSplitTimeEnabled();

  const [visitPhase, setVisitPhase] = useState<VisitPhase>('pending');
  const [availableTasks, setAvailableTasks] = useState<{ sets: PendingTask[]; calls: PendingTask[] }>({ sets: [], calls: [] });
  const [lastCompletedTask, setLastCompletedTask] = useState<CompletedTask | null>(null);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);

  // Drive confirmation flow state
  const [showDriveConfirmation, setShowDriveConfirmation] = useState(false);
  const [pendingDriveData, setPendingDriveData] = useState<{
    startTime: Date | null;
    endTime: Date | null;
  } | null>(null);

  // Get associate ID from localStorage (set during login)
  const getAssociateId = useCallback((): number | null => getEffectiveAssociateId(), []);

  // Get manual entry params from URL
  const manualDate = searchParams.get('manualDate'); // YYYY-MM-DD for retroactive entries
  const skipGpsCheck = searchParams.get('skipGpsCheck') === 'true';

  // What the rep is offered on arrival — one rule, stated in utils/arrivalGate
  // so it can be tested. Two inline JSX conditions used to disagree about
  // whether the rep had clocked in (#3519).
  const offerOnArrival = arrivalOffer({
    storeStatus: store?.status ?? '',
    dayStatus: dayData.status,
    skipGpsCheck,
    isCheckingIn,
    showOdometerEntry,
    showStartDayFlow,
  });
  const isManualEntry = !!manualDate;

  // Enhanced manual entry params (from Add Call/Set flow with pre-entered data)
  const isFullManualEntry = searchParams.get('manualEntry') === 'true';
  const manualTimeIn = searchParams.get('timeIn') || ''; // HH:MM
  const manualTimeOut = searchParams.get('timeOut') || ''; // HH:MM
  const manualDriveStartOdo = searchParams.get('driveStartOdo');
  const manualDriveEndOdo = searchParams.get('driveEndOdo');
  const manualTeamProject = searchParams.get('teamProject') === 'true';
  const manualTeamLeader = searchParams.get('teamLeader') === 'true';
  const manualNotes = searchParams.get('notes') || '';
  // Why this visit exists on a store/project the rep's schedule doesn't carry.
  // An audit fact with its own CallLog column — it must not ride notes, which
  // checkout rewrites wholesale. `offScheduleReason` = the live check-in
  // handoff; `unassignedReason` = the legacy retroactive modal's label.
  const offScheduleReason = searchParams.get('offScheduleReason') || '';
  const unassignedReasonParam = searchParams.get('unassignedReason') || '';

  // ==========================================================================
  // Consolidated manual entry path (#1863) — coexists with legacy path above.
  //
  // The new pre-flight at /manual-entry/new creates the CallLog server-side
  // with stcalltime + drive odo, sets ManualVisitContext, then navigates here.
  // The legacy `isManualEntry` / `isFullManualEntry` branches above DON'T fire
  // because the new pre-flight never sets manualDate / manualEntry query
  // params. Everything below is additive — Phase 3 (#1865) will delete the
  // legacy blocks once the new flow is validated in production.
  //
  // The clock hook returns injected timestamps (timeIn / timeOut from the
  // pre-flight) for visit-event timestamps when manualVisit is set, and
  // returns new Date() otherwise — so the live workflow is unaffected.
  // ==========================================================================
  const { manualVisit, clearManualVisit } = useManualVisit();
  const clock = useVisitClock();
  const manualVisitInitRan = useRef(false);

  // Site tasks (#1955/#1995): one per project, sourced from the authoritative
  // useVisitTask state (currentTask/pausedTasks/completedTasks) merged with the
  // store's scheduled projects (todo). Re-derives when multiTaskState changes,
  // so pausing/finishing a task updates the list. Each task carries the site
  // key (legacyStoreId + retailerId) + projectId; names are display-only.
  const siteTasks: SiteTask[] = useMemo(
    () => {
      // Build the authoritative schedule (every task at the store + its REAL
      // name) from pending-tasks first, falling back to the CallLog-derived
      // store.projects. Without this, todo tasks with no CallLog yet are
      // missing from "Tasks at this store" and names fall back to "#<id>"
      // ("#1102 #1102") — #2012.
      const named = new Map<number, string>();
      [...availableTasks.calls, ...availableTasks.sets].forEach((t) => {
        if (t.project_id && t.project_description) named.set(t.project_id, t.project_description);
      });
      (store?.projects ?? []).forEach((p) => {
        if (!named.has(p.id)) named.set(p.id, p.name);
      });
      const scheduledProjects = [...named.entries()].map(([id, name]) => ({ id, name }));
      return deriveSiteTasks({
        legacyStoreId: store?.legacyStoreId ?? 0,
        retailerId: store?.retailerId ?? null,
        visitState: multiTaskState,
        scheduledProjects,
      });
    },
    [store?.legacyStoreId, store?.retailerId, store?.projects, multiTaskState, availableTasks],
  );

  // Resolve a project's REAL display name by id from the authoritative
  // pending-tasks list, falling back to a stored description, then "#<id>".
  // Used by the active/paused chips + header so they never render "#1102 #1102"
  // when the stored project_description is a bare id fallback (#2012).
  const projectDisplayName = useCallback(
    (projectId: number | null | undefined, fallback?: string | null): string => {
      if (!projectId) return fallback || '';
      const t = [...availableTasks.calls, ...availableTasks.sets].find((x) => x.project_id === projectId);
      if (t?.project_description && t.project_description !== `#${projectId}`) return t.project_description;
      const fromStore = (store?.projects ?? []).find((p) => p.id === projectId);
      if (fromStore?.name && fromStore.name !== `#${projectId}`) return fromStore.name;
      if (fallback && fallback !== `#${projectId}`) return fallback;
      return `#${projectId}`;
    },
    [availableTasks, store?.projects],
  );

  // State for tracking manual entry auto-creation
  const [manualEntryCreated, setManualEntryCreated] = useState(false);
  const [isCreatingManualEntry, setIsCreatingManualEntry] = useState(false);
  const manualEntryEffectRan = useRef(false);
  // Stable idempotency key for the verified-manual-entry createCallLog
  // path (the auto-create effect + the verification-modal submit).
  // Was missing — could create duplicate rows if the auto-create
  // effect re-ran and timed out, or if the rep tapped through the
  // verification modal twice in a row. (#1892 sibling)
  const manualEntryKeyRef = useRef<string>(
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `manual-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
  // Ref version of manualEntryCreated - used in fetch effect to check current value
  // (state captured in closure won't update during async execution)
  const manualEntryCreatedRef = useRef(false);
  // Dedicated error state for manual-entry create failures (timeout, network,
  // 5xx) so we can render a retry UI separate from the generic page errors. #1850
  const [manualEntryError, setManualEntryError] = useState<string | null>(null);

  // 20s ceiling on the create-CallLog request. Anything beyond this is
  // either a hung connection or a backend stall; better to fail visibly
  // and let the rep retry than to spin "Creating Entry..." forever. #1850
  const MANUAL_ENTRY_TIMEOUT_MS = 20_000;

  // Ref to track current store state - used in handleCheckoutComplete to access
  // latest callLogId since the function may capture a stale closure of store state
  const storeRef = useRef<StoreData | null>(null);

  // Keep storeRef in sync with store state
  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  // Fetch store data from API
  useEffect(() => {
    async function fetchStoreData() {
      console.log('[Fetch] fetchStoreData called, storeId:', storeId, 'manualEntryCreated:', manualEntryCreated);
      if (!storeId) return;

      // Skip fetch if manual entry was already created - auto-create effect sets up correct state
      // The API would return edcalltime for full manual entries, causing status='completed'
      // but we want status='in_progress' until checkout is done
      // Use ref to check current value (state captured in closure won't update during async)
      if (manualEntryCreatedRef.current) {
        console.log('[Fetch] Skipping - manualEntryCreatedRef is true');
        return;
      }
      // New consolidated manual entry (#1863): once manualVisitInit has set
      // store.status to in_progress, don't re-fetch — fetchStoreData's
      // derived status would clobber the override (the legacy CallLog list
      // endpoint hides in-progress visits, so the derived status comes back
      // as 'completed' from a stale CallLog at the same store).
      if (manualVisitInitRan.current) {
        console.log('[Fetch] Skipping - manualVisitInitRan is true');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const numericStoreId = parseInt(storeId, 10);
        // For manual entries, use the manual date; otherwise use today.
        // The consolidated manual entry path (#1863) carries date via context
        // (manualVisit) rather than URL — fall through to it when no legacy
        // manualDate query param is set, so historical CallLog data loads
        // for the right day.
        const todayStr = manualDate || manualVisit?.date || formatDateToLocal(new Date());

        // Task/adoption info from the CURRENT URL params (passed from Set/Route
        // navigation, queue-only check-in handoff, etc.). Single-sourced with
        // the cold-load hydrate so both derive nav identically (#2066).
        const {
          projectIdFromNav,
          distributorIdFromNav,
          taskIdFromNav,
          taskTypeFromNav,
          projectDescFromNav,
          clientNameFromNav,
          callLogIdFromNav,
          visitLocalIdFromNav,
          teamProjectFromNav,
          teamLeadFromNav,
        } = deriveNavFromSearchParams(searchParams);

        // #2087 (diag #48): /api/orders/ is deliberately NOT in this barrier.
        // It took 7.7s on a slow link while getStore/getCallLogs returned in
        // ~400ms, and the only thing derived from it is the hasOpenOrders
        // badge — it must not gate store paint, serverConfirmed (which gates
        // task adoption, #2066), or the pending-tasks fetch. It resolves
        // independently and patches state whenever it lands.
        let resolvedHasOpenOrders: boolean | null = null;
        ordersApi.getOrders({ storeId: numericStoreId, status: 'OPEN' })
          .then((orders) => {
            resolvedHasOpenOrders = orders.length > 0;
            // If the barrier already setStore'd (orders slower than the
            // barrier), patch the live store + memory cache in place.
            setStore((prev) => {
              if (!prev) return prev;
              const next = { ...prev, hasOpenOrders: resolvedHasOpenOrders! };
              storeCache.set(storeCacheKey, next);
              return next;
            });
          })
          .catch(() => {
            // Badge is best-effort — a failed orders fetch must not surface.
          });

        // Make the remaining API calls in PARALLEL for faster loading
        // Pass projectId to getStore to disambiguate when legacystoreid is not unique
        const [apiStoreResult, callLogsResult] = await Promise.allSettled([
          storesApi.getStore(numericStoreId, projectIdFromNav || undefined),
          callLogsApi.getCallLogs(todayStr),
        ]);

        // Extract results (handle failures gracefully)
        const apiStore: ApiStore | null = apiStoreResult.status === 'fulfilled' ? apiStoreResult.value : null;
        const callLogs = callLogsResult.status === 'fulfilled' ? callLogsResult.value : [];
        // Orders usually outlives the barrier; if it happened to win the race,
        // pick its answer up here so the patch above isn't clobbered below.
        const hasOpenOrders = resolvedHasOpenOrders ?? false;

        // Find call logs for this store (actual store VISITs, not drive time).
        // The API returns multiple entries per call log - drive entries and work entries.
        // Drive entries have driveodostart/driveodoend > 0 and a "Drive Time" description.
        // Work (store visit) entries have driveodostart=0, driveodoend=0.
        //
        // For multi-project stores there are MULTIPLE work entries (one per
        // project task). We need to look at all of them to determine store
        // status, not just the first match - the old single-find logic flipped
        // the whole store to 'completed' as soon as one task's CallLog had
        // edcalltime, even if the rep was actively in another task. #1849
        const isStoreVisitEntry = (log: typeof callLogs[number]) => {
          if (log.store_legacystoreid !== numericStoreId) return false;
          const isDriveEntry = (log.driveodostart && log.driveodostart > 0) ||
                               (log.driveodoend && log.driveodoend > 0) ||
                               (log.description_of_activity?.includes('Drive Time'));
          return !isDriveEntry;
        };

        // Scope to the visit's date (#1994). getCallLogs returns the whole
        // work week; without this, a PRIOR-DAY completed visit for one of a
        // multi-project store's other projects (e.g. last week's #1068 visit)
        // leaks in and flips the store to "Visit Completed", blocking the rep
        // from working the task they just checked into today. dtecall is
        // "MM/DD/YYYY" from the API; todayStr is "YYYY-MM-DD".
        const tp = todayStr.split('-');
        const targetMMDDYYYY = tp.length === 3 ? `${tp[1]}/${tp[2]}/${tp[0]}` : todayStr;
        const isTargetDate = (log: typeof callLogs[number]) => {
          const d = log.dtecall;
          if (!d) return false;
          return d === targetMMDDYYYY || (typeof d === 'string' && d.startsWith(todayStr));
        };

        const storeCallLogs = callLogs.filter(isStoreVisitEntry).filter(isTargetDate);

        // Pick a representative CallLog for fields like lat/lng/callLogId.
        // Prefer the one matching projectIdFromNav (single-task scenario) but
        // fall back to any if none matches. Status is derived from the whole
        // collection below, not from this one.
        const storeCallLog =
          (projectIdFromNav
            ? storeCallLogs.find((log) => log.project_id === projectIdFromNav)
            : null) || storeCallLogs[0] || undefined;

        // Determine store status from the WHOLE collection:
        //   - no logs -> pending
        //   - any log started but not ended -> in_progress
        //   - all logs ended -> completed
        // checkInTime = earliest stcalltime across the collection
        // checkOutTime = latest edcalltime, but only if every log is closed
        let status: StoreData['status'] = 'pending';
        let checkInTime: string | null = null;
        let checkOutTime: string | null = null;

        if (storeCallLogs.length > 0) {
          const startedLogs = storeCallLogs.filter((l) => l.stcalltime);
          const anyOpen = startedLogs.some((l) => !l.edcalltime);

          // Earliest start across the visit
          if (startedLogs.length > 0) {
            checkInTime = startedLogs
              .map((l) => l.stcalltime!)
              .sort()[0];
          }

          if (anyOpen) {
            status = 'in_progress';
          } else if (startedLogs.length > 0) {
            // Every started log is also closed - store visit is complete
            status = 'completed';
            checkOutTime = startedLogs
              .map((l) => l.edcalltime!)
              .sort()
              .slice(-1)[0];
          }
          // else: logs exist but none have stcalltime set - leave as pending
        }

        // Queue-only check-in handoff (#1987 Phase 3): the rep checked in
        // locally and we navigated here on the visit localId BEFORE the POST
        // synced, so the server has no CallLog yet and the derivation above
        // leaves status 'pending'. Trust the local handoff and show
        // in_progress so the auto-start effect adopts the active task. (The
        // day-tracking fallback below covers the already-synced case.)
        if (visitLocalIdFromNav && status === 'pending') {
          status = 'in_progress';
          if (!checkInTime) checkInTime = new Date().toISOString();
        }

        // Build store data (use denormalized fields from call log if store API not available)
        // Use calllog lat/lon if available (from GPS check-in), otherwise use store coordinates
        const storeLat = apiStore?.latitude || storeCallLog?.lat || 0;
        const storeLng = apiStore?.longitude || storeCallLog?.lon || 0;

        // Construct display name: "RetailerAbbrev #LegacyStoreID"
        // storename from API contains just the retailer abbreviation (e.g., "ECK", "CVS")
        const storeNumber = apiStore?.legacystoreid || storeCallLog?.store_legacystoreid || numericStoreId;
        const retailerAbbrev = apiStore?.storename || storeCallLog?.retailer_abbreviation || 'Store';
        const displayName = `${retailerAbbrev} #${storeNumber}`;

        const storeData: StoreData = {
          // IMPORTANT: Use actual store PK from API, not legacystoreid from URL
          // The URL uses legacystoreid for display, but backend APIs expect the actual PK
          id: apiStore?.storeid || numericStoreId,
          // Legacy store ID is used by products API for filtering
          legacyStoreId: apiStore?.legacystoreid || storeNumber,
          // Site key = legacyStoreId + retailerId (#1944/#1995); retailerId
          // disambiguates the legacystoreid collision across retailers.
          retailerId: apiStore?.retailerid ?? null,
          name: displayName,
          number: String(storeNumber),
          address: apiStore?.address || '',
          city: apiStore
            ? `${apiStore.city}, ${apiStore.state} ${apiStore.zip}`
            : storeCallLog?.store_city || '',
          phone: apiStore?.phone || '',
          lat: storeLat,
          lng: storeLng,
          status,
          checkInTime,
          checkOutTime,
          callLogId: storeCallLog?.calllogid || null,
          hasOpenOrders,
          // Build one entry per distinct project at this store. Each
          // project's `completed` flag is derived from its OWN CallLog's
          // edcalltime, not the store-wide status - so a multi-project
          // store can show task A completed, task B in progress. #1849
          projects: storeCallLogs.length > 0
            ? Array.from(
                new Map(
                  storeCallLogs
                    .filter((log) => log.project_id)
                    .map((log) => [
                      log.project_id,
                      {
                        id: log.project_id!,
                        name:
                          log.description_of_activity ||
                          (log.project_id === projectIdFromNav ? projectDescFromNav : null) ||
                          `#${log.project_id}`,
                        completed: !!log.edcalltime,
                      },
                    ])
                ).values()
              )
            : projectIdFromNav
              ? [{ id: projectIdFromNav, name: projectDescFromNav || `#${projectIdFromNav}`, completed: false }]
              : [],
          projectIdFromNav,
          distributorIdFromNav,
          taskIdFromNav,
          taskTypeFromNav,
          projectDescFromNav,
          clientNameFromNav,
          callLogIdFromNav,
          visitLocalIdFromNav,
          teamProjectFromNav,
          teamLeadFromNav,
        };

        // Check again before setStore - auto-create may have run while we were awaiting API
        if (manualEntryCreatedRef.current) {
          console.log('[Fetch] Skipping setStore - manualEntryCreatedRef became true during fetch');
          return;
        }

        // Use functional update to preserve callLogId if already set by auto-create effect
        // This prevents race conditions where the fetch re-runs and overwrites the callLogId
        console.log('[Fetch] About to setStore, storeCallLog:', storeCallLog?.calllogid, 'status from API:', status);
        // Cache for an instant paint on the next remount (return from OrderEntry, etc.).
        storeCache.set(storeCacheKey, storeData);
        // #2066: persist the SERVER snapshot (nav stripped) so a cold reload can
        // paint instantly, and mark adoption as safe to act on confirmed status.
        void cacheStoreDetailSnapshot(storeCacheKey, serverSnapshot(storeData));
        setServerConfirmed(true);
        setStore(prev => {
          console.log('[Fetch] setStore callback - prev.callLogId:', prev?.callLogId, 'prev.status:', prev?.status);
          // If we already have a callLogId (e.g., from auto-create effect) and API doesn't have a newer one
          if (prev?.callLogId && !storeCallLog?.calllogid) {
            console.log('[Fetch] Preserving prev state');
            return {
              ...storeData,
              callLogId: prev.callLogId,
              status: prev.status,
              checkInTime: prev.checkInTime,
              checkOutTime: prev.checkOutTime,
            };
          }
          console.log('[Fetch] Using API data, status:', storeData.status);
          return storeData;
        });
        // Store the full callLog for edit functionality
        if (storeCallLog) {
          setEditingCallLog(storeCallLog);
        }

        // (Checked-in reconciliation from day-tracking moved to its own effect
        // below so day-state updates no longer trigger a full store re-fetch.)
      } catch (err) {
        console.error('Failed to fetch store data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load store data');
      } finally {
        setIsLoading(false);
      }
    }

    fetchStoreData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, searchParams.get('projectId'), manualDate, manualVisit?.date]);

  // #2066: keep serverConfirmed in step with the cache for the current key. A
  // same-session in-memory hit stays "confirmed" (adopt instantly); switching to
  // a key with no in-memory entry resets to unconfirmed until fetch confirms.
  useEffect(() => {
    setServerConfirmed(storeCache.has(storeCacheKey));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCacheKey]);

  // #2066: cold-load instant paint. If the in-memory cache missed (e.g. the PWA
  // was reloaded), hydrate the last-known SERVER snapshot from IDB so a return to
  // an active task paints immediately instead of the full-screen spinner. Nav
  // adoption fields come from the CURRENT url (never the snapshot), and adoption
  // stays gated on serverConfirmed, so a stale snapshot can't resume a wrong task.
  useEffect(() => {
    if (store) return; // in-memory cache already painted synchronously
    let cancelled = false;
    (async () => {
      const snap = await getCachedStoreDetailSnapshot<StoreData>(storeCacheKey);
      if (cancelled || !snap) return;
      setStore((prev) => prev ?? { ...snap, ...deriveNavFromSearchParams(searchParams) });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCacheKey]);

  // Reconcile "currently checked in" from day-tracking WITHOUT re-fetching the
  // store. Previously dayData.timeEntries / currentStoreId were deps of the fetch
  // effect, so every day-state tick (timer, GPS write, check-in) re-ran the full
  // fetch and flashed the loading spinner. This lightweight effect just patches
  // the status in place. (#perf store-detail-loading)
  useEffect(() => {
    if (!store || store.status !== 'pending' || !storeId) return;
    const numericStoreId = parseInt(storeId, 10);
    if (dayData.currentStoreId !== numericStoreId) return;
    const currentEntry = dayData.timeEntries.find(
      (e) => e.type === 'store' && e.storeId === numericStoreId && !e.endTime
    );
    if (currentEntry) {
      setStore((prev) =>
        prev ? { ...prev, status: 'in_progress', checkInTime: currentEntry.startTime.toISOString() } : prev
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.status, storeId, dayData.currentStoreId, dayData.timeEntries]);

  // Fetch store notes (uses resolved PK, not URL param which is legacystoreid)
  useEffect(() => {
    async function fetchNotes() {
      if (!store?.id) return;

      setIsLoadingNotes(true);
      try {
        const notes = await storesApi.getStoreNotes(store.id);
        setStoreNotes(
          notes.map((n) => ({
            id: n.id,
            storeId: n.storeId,
            authorName: n.authorName,
            authorId: n.authorId,
            content: n.content,
            createdAt: n.createdAt,
            isPinned: n.isPinned,
          }))
        );
      } catch {
        // Notes API might not exist yet
        setStoreNotes([]);
      } finally {
        setIsLoadingNotes(false);
      }
    }

    fetchNotes();
  }, [store?.id]);

  // Geocode store address if coordinates are missing
  useEffect(() => {
    async function geocodeStoreAddress() {
      if (!store) return;
      // Skip if we already have valid coordinates
      if (store.lat !== 0 && store.lng !== 0) return;
      // Skip if we don't have an address to geocode
      if (!store.address || !store.city) return;

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) return;

      try {
        const fullAddress = `${store.address}, ${store.city}`;
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`
        );
        const data = await response.json();

        if (data.status === 'OK' && data.results?.[0]?.geometry?.location) {
          const { lat, lng } = data.results[0].geometry.location;
          // Update local state with geocoded coordinates
          setStore((prev) =>
            prev ? { ...prev, lat, lng } : null
          );
          console.log(`Geocoded store ${store.number}: ${lat}, ${lng}`);

          // Persist the geocoded coordinates through the sync queue (#1987):
          // the queue is the SOLE server-write path. Works online or offline.
          if (store.id > 0) {
            import('../sync/writeOps')
              .then(({ writeOps }) =>
                writeOps.patchStoreCoordinates.apply({ storeId: store.id, latitude: lat, longitude: lng })
              )
              .catch((enqErr) => console.warn('coordinate enqueue failed (non-fatal):', enqErr));
          }
        }
      } catch (err) {
        // Silently fail - geocoding is optional enhancement
        console.log('Geocoding failed for store:', store.number, err);
      }
    }

    geocodeStoreAddress();
  }, [store?.id, store?.lat, store?.lng, store?.address, store?.city, isOnline]);

  // Effect: Fetch survey questions for the current project. #2088 (diag #48):
  // outcomes are explicit — loaded / none / error. A fetch failure surfaces a
  // retryable error state and NEVER substitutes demo questions (reps were
  // answering fake questions on a real project when the endpoint 400'd).
  useEffect(() => {
    // Get projectId: prefer active task's project, then nav, then first project
    const projectId = multiTaskState.currentTask?.project_id
      || store?.projectIdFromNav
      || store?.projects?.[0]?.id;
    if (!projectId) return;
    // Dedupe (#2088): `store` gets a new identity on every setStore (cache
    // paint, server confirm, async hasOpenOrders patch) — diag #48 showed the
    // same project fetched 3x in one load. Refetch only for a NEW project, a
    // retry, or after an error (the ref is cleared on failure below).
    //
    // No cleanup-cancellation here: a dep-change re-run would cancel the
    // in-flight fetch while the dedupe ref blocks the refetch — the survey
    // would never arrive. Staleness is checked at APPLY time instead: the
    // result lands only if its project is still the one on screen.
    surveyDesiredProject.current = projectId;
    if (surveyFetchedFor.current === projectId) return;
    surveyFetchedFor.current = projectId;

    setIsLoadingSurvey(true);
    void loadProjectSurvey(projectId).then((result) => {
      if (surveyDesiredProject.current !== projectId) return;
      if (result.kind === 'loaded') {
        setSurveyId(result.surveyId);
        setSurveyQuestions(result.questions);
        setSurveyError(false);
      } else if (result.kind === 'none') {
        // Project genuinely has no survey — a normal state, clear everything.
        setSurveyQuestions([]);
        setSurveyId(null);
        setSurveyError(false);
      } else {
        setSurveyQuestions([]);
        setSurveyId(null);
        setSurveyError(true);
        // Allow the next effect run (store update or manual Retry) to refetch.
        surveyFetchedFor.current = null;
      }
      setIsLoadingSurvey(false);
    });
  }, [multiTaskState.currentTask?.project_id, store?.projectIdFromNav, store?.projects, surveyRetryNonce]);

  // Manual retry for a failed survey fetch (#2088).
  const retrySurvey = useCallback(() => {
    surveyFetchedFor.current = null;
    setSurveyError(false);
    setSurveyRetryNonce((n) => n + 1);
  }, []);

  // Shared survey section for both set and call task views (#2088): spinner →
  // retryable error → real questions → explicit no-survey state. Demo
  // questions are not reachable from any production path. (A function, not an
  // eager JSX const — handleSurveyResponseChange is declared further down.)
  const renderSurveySection = () => isLoadingSurvey ? (
    <div className="flex items-center justify-center py-4">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
      <span className="ml-2 text-sm text-slate-500">Loading survey...</span>
    </div>
  ) : surveyError ? (
    <div className="rounded-xl bg-white p-4 shadow-card" data-testid="survey-load-error">
      <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">Survey Questions</h3>
      <p className="mt-2 text-sm text-red-600">Couldn't load the survey for this task.</p>
      <button
        onClick={retrySurvey}
        className="mt-3 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
      >
        Retry
      </button>
    </div>
  ) : surveyQuestions.length > 0 ? (
    <SurveyQuestions
      questions={surveyQuestions}
      responses={surveyResponses}
      onResponseChange={handleSurveyResponseChange}
      disabled={false}
    />
  ) : (
    <div className="rounded-xl bg-white p-4 shadow-card" data-testid="no-survey">
      <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">Survey Questions</h3>
      <p className="mt-2 text-sm text-slate-500">No survey for this task.</p>
    </div>
  );

  // ==========================================================================
  // Consolidated manual entry initializer (#1863)
  //
  // When the new pre-flight has set ManualVisitContext + the store has loaded,
  // bring the visit straight into the canonical task-active phase. The
  // pre-flight already POSTed the CallLog with stcalltime + drive odo, so
  // there's nothing for the rep to "check in" — they're effectively at the
  // task work step.
  //
  // The synthetic task carries the rep's manual project/desc and is anchored
  // to the pre-flight's CallLog id. startTask is given the manual time-in as
  // its start-time override so the task's recorded duration matches what the
  // rep entered, not the moment they happened to land on this page.
  //
  // This effect deliberately does nothing on the live path (manualVisit is
  // null) and runs once per mount via manualVisitInitRan.
  // ==========================================================================
  useEffect(() => {
    if (!manualVisit) return;
    if (!store) return;
    if (manualVisitInitRan.current) return;
    if (visitPhase !== 'pending') return;

    manualVisitInitRan.current = true;

    // A manual entry "arrives" at the store without a live GPS check-in. It is
    // still NOT checkIntoStore — that stamps live GPS as the arrival, which a
    // rep entering times by hand never gave us. But it must reach the day
    // chain at the instant the rep ASSERTED, not the moment they submitted:
    // stopDriving() closed the drive at new Date(), so an arrival of 09:15
    // filed at 09:40 wrote 09:15 to the CallLog and 09:40 to everything else
    // (#3519). This also creates the store leg the manual path never made.
    void injectManualVisit({
      storeId: store.id,
      storeName: store.name,
      projectId: manualVisit.projectId,
      arrivedAt: new Date(manualVisit.timeIn),
      departedAt: manualVisit.timeOut ? new Date(manualVisit.timeOut) : null,
      driveStartOdometer: manualVisit.driveStartOdometer,
      driveEndOdometer: manualVisit.driveEndOdometer,
    });

    // Override store.status to in_progress + plug in the CallLog id and
    // check-in time from the pre-flight. The legacy CallLog list endpoint
    // hides in-progress visits (stcalltime set but edcalltime not), so
    // fetchStoreData's derived status comes back 'pending' even though the
    // CallLog exists on the server. Without this override the rep would see
    // the GPS check-in screen instead of the task UI.
    setStore((prev) =>
      prev
        ? {
            ...prev,
            status: 'in_progress',
            checkInTime: manualVisit.timeIn,
            callLogId: manualVisit.callLogId,
            // Queue-only manual entry (#2013): the visit is identified by its
            // localId until the createManualVisit op syncs. Adopt it so every
            // dependent write keys on the same uuid.
            visitLocalIdFromNav: manualVisit.visitLocalId ?? null,
          }
        : prev,
    );

    startMultiTaskVisit(store.id);
    const syntheticTask: PendingTask = {
      task_type: 'call',
      task_id: 0,
      project_id: manualVisit.projectId,
      project_description: manualVisit.projectDescription || store.projects[0]?.name || 'Visit',
      client_name: null,
      distributor_id: manualVisit.distributorId ?? null,
    };
    startTask(
      syntheticTask,
      manualVisit.callLogId ?? undefined,
      new Date(manualVisit.timeIn),
      // Adopt the localId the createManualVisit op minted so this visit and
      // its dependent writes resolve the server calllogid lazily (#2013).
      manualVisit.visitLocalId ? { adoptUUID: manualVisit.visitLocalId } : undefined,
    );
    setVisitPhase('task_active');

    // Background fetch of other available tasks at this store (for the
    // "Start Another Task" affordance after this one finishes).
    void (async () => {
      try {
        const projectId = manualVisit.projectId;
        const response = await storeTasksApi.getPendingTasks(store.id, manualVisit.date, projectId);
        setAvailableTasks({ sets: response.sets, calls: response.calls });
      } catch (err) {
        console.warn('[ManualVisit] failed to load other tasks at store', err);
        setAvailableTasks({ sets: [], calls: [] });
      }
    })();
  }, [manualVisit, store, visitPhase, startMultiTaskVisit, startTask, dayData.currentActivity, injectManualVisit]);

  // Extracted: actually create the CallLog and run the post-create flow
  // (state updates, multi-task setup, background task fetch). Called from
  // both the auto-create effect (verified path) and the verification modal
  // submit handler (unverified path, with override reason + code).
  const finalizeManualEntryCreate = useCallback(async (callLogData: CallLogCreate) => {
    if (!store || !manualDate || !manualTimeIn) return;
    // Same priority order as performCheckIn — nav intent wins over the
    // store's first listed project. Fixes the Publix 886 / Rob bug for
    // the verified-manual-entry path too.
    const projectId = store.projectIdFromNav || store.projects[0]?.id || 1;
    const stcalltime = `${manualDate}T${manualTimeIn}:00`;
    const edcalltime = manualTimeOut ? `${manualDate}T${manualTimeOut}:00` : undefined;

    setIsCreatingManualEntry(true);
    setManualEntryError(null);

    // Race the create-CallLog request against a 20s timeout. If the
    // request hangs (server-side stall, maintenance window, lost
    // connectivity mid-flight) we abort and surface a retry UI instead
    // of spinning "Creating Entry..." indefinitely. #1850
    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      abortController.abort(new DOMException('manual entry create timed out', 'TimeoutError'));
    }, MANUAL_ENTRY_TIMEOUT_MS);

    try {
      const callLogResponse = await callLogsApi.createCallLog(callLogData, {
        signal: abortController.signal,
        idempotencyKey: manualEntryKeyRef.current,
      });
      // API returns wrapped response: { status, message, data: CallLog }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const callLog = (callLogResponse as any).data || callLogResponse;
      const createdCallLogId = callLog.calllogid;

      setStore(prev => {
        const newStore = prev ? {
          ...prev,
          callLogId: createdCallLogId,
          status: 'in_progress' as const,
          checkInTime: stcalltime,
          ...(edcalltime && { checkOutTime: edcalltime }),
        } : null;
        return newStore;
      });

      manualEntryCreatedRef.current = true;
      setManualEntryCreated(true);
      console.log('[Auto-create] Set manualEntryCreated to true');

      startMultiTaskVisit(store.id);
      const syntheticTask: PendingTask = {
        task_type: 'call',
        task_id: 0,
        project_id: projectId,
        // Name must follow the SAME projectId — never positional (#1996).
        project_description: resolveProjectName(
          projectId,
          store.projects,
          store.projectIdFromNav,
          store.projectDescFromNav,
        ),
        client_name: store.clientNameFromNav || null,
        distributor_id: store.distributorIdFromNav || null,
      };
      startTask(syntheticTask, createdCallLogId || undefined);
      setVisitPhase('task_active');

      try {
        const response = await storeTasksApi.getPendingTasks(store.id, manualDate || undefined, projectId || undefined);
        setAvailableTasks({ sets: response.sets, calls: response.calls });
      } catch (err) {
        console.error('Failed to fetch available tasks:', err);
        setAvailableTasks({ sets: [], calls: [] });
      }
    } catch (err) {
      console.error('Failed to create manual entry:', err);
      let errorMessage = 'Failed to create call log';
      if (err && typeof err === 'object' && 'data' in err) {
        const apiErr = err as { data?: { non_field_errors?: string[]; [key: string]: unknown } };
        if (apiErr.data?.non_field_errors?.length) {
          errorMessage = apiErr.data.non_field_errors.join(', ');
        } else if (apiErr.data && typeof apiErr.data === 'object') {
          const firstError = Object.values(apiErr.data).find(v => Array.isArray(v) && v.length > 0);
          if (firstError && Array.isArray(firstError)) {
            errorMessage = firstError[0];
          }
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }

      if (errorMessage.toLowerCase().includes('overlapping')) {
        const conflict = parseConflictFromError(errorMessage);
        if (conflict) {
          setTimeConflicts([conflict]);
          setConflictTimeIn(manualTimeIn);
          setConflictTimeOut(manualTimeOut);
          setShowTimeConflictEditor(true);
          setError(null);
        } else {
          setError(errorMessage);
        }
      } else {
        // Network / timeout / 5xx: route to the manual-entry-specific
        // retry UI so the rep can recover without losing the form data
        // already on this page. AbortError + TimeoutError get a friendlier
        // message than the raw DOMException string. #1850
        const isTimeout =
          err instanceof DOMException &&
          (err.name === 'TimeoutError' || err.name === 'AbortError');
        const isNetwork =
          err instanceof TypeError && /fetch|network/i.test(err.message || '');

        if (isTimeout || isNetwork) {
          setManualEntryError(
            isTimeout
              ? "We didn't hear back from the server within 20 seconds. Your connection may be slow or the server may be busy. Tap Try Again to retry."
              : "Couldn't reach the server. Check your connection and try again."
          );
          // Reset the once-fired ref so the retry button can re-trigger
          // the create flow without forcing a full page reload.
          manualEntryEffectRan.current = false;
          manualEntryCreatedRef.current = false;
        } else {
          setError(errorMessage);
        }
      }
    } finally {
      window.clearTimeout(timeoutId);
      setIsCreatingManualEntry(false);
    }
  }, [store, manualDate, manualTimeIn, manualTimeOut, startMultiTaskVisit, startTask]);

  // Retry handler for the manual-entry create after a timeout/network error.
  // Clears the error and resets the once-fired ref so the auto-create
  // effect re-runs naturally. #1850
  const retryManualEntryCreate = useCallback(() => {
    setManualEntryError(null);
    manualEntryEffectRan.current = false;
    manualEntryCreatedRef.current = false;
    setManualEntryCreated(false);
  }, []);

  // Effect: Auto-create call log for full manual entry (from Add Call/Set flow).
  // Attempts GPS capture first (#1779). If presence cannot be verified
  // (off-site, no GPS, weak accuracy, store missing coords), defers creation
  // and opens GpsVerificationReasonModal to require an override reason.
  useEffect(() => {
    if (!isFullManualEntry) return;
    if (manualEntryEffectRan.current || manualEntryCreated || isCreatingManualEntry) return;
    if (!store || isLoading) return;
    if (!manualDate || !manualTimeIn) return;

    manualEntryEffectRan.current = true;
    manualEntryCreatedRef.current = true;

    (async () => {
      const associateId = getAssociateId();
      if (!associateId) {
        setError('Unable to create entry: Please log out and log back in.');
        return;
      }

      const projectId = store.projectIdFromNav || store.projects[0]?.id || 1;
      const stcalltime = `${manualDate}T${manualTimeIn}:00`;
      const edcalltime = manualTimeOut ? `${manualDate}T${manualTimeOut}:00` : undefined;

      const baseCallLogData: CallLogCreate = {
        store_id: store.id,
        project_id: projectId,
        associate_id: associateId,
        dtecall: `${manualDate}T${manualTimeIn}:00`,
        stcalltime,
        ...(edcalltime && { edcalltime }),
        teamproject: manualTeamProject,
        teamleader: manualTeamLeader,
        notes: manualNotes || undefined,
        off_schedule_reason: unassignedReasonParam || undefined,
        ...(manualDriveStartOdo && manualDriveEndOdo && {
          driveodostart: parseInt(manualDriveStartOdo, 10),
          driveodoend: parseInt(manualDriveEndOdo, 10),
        }),
      };

      // Attempt GPS capture for verification. Don't block creation if it fails —
      // computeVerificationState handles the null/denied case.
      setIsCreatingManualEntry(true);
      const pos = await getVerificationPosition().catch(() => null);
      const verification = computeVerificationState(pos, store.lat, store.lng);
      setIsCreatingManualEntry(false);

      if (verification.code === 'verified') {
        const gpsFields = pos
          ? {
              lat: Math.round(pos.latitude * 1000000) / 1000000,
              lon: Math.round(pos.longitude * 1000000) / 1000000,
              ...(pos.accuracy != null && { gps_accuracy: Math.round(pos.accuracy * 100) / 100 }),
            }
          : {};
        await finalizeManualEntryCreate({ ...baseCallLogData, ...gpsFields });
      } else {
        setPendingManualEntry({
          baseCallLogData,
          position: pos,
          code: verification.code,
          distance: verification.distance,
        });
        setShowVerificationModal(true);
      }
    })();
  }, [isFullManualEntry, manualEntryCreated, isCreatingManualEntry, store, isLoading, manualDate, manualTimeIn, manualTimeOut, manualDriveStartOdo, manualDriveEndOdo, manualTeamProject, manualTeamLeader, manualNotes, getAssociateId, getVerificationPosition, finalizeManualEntryCreate]);

  // Verification modal: user provided a reason — enrich and create.
  const handleVerificationSubmit = useCallback(async (reasonText: string) => {
    if (!pendingManualEntry) return;
    setShowVerificationModal(false);
    const { baseCallLogData, position, code } = pendingManualEntry;
    const gpsFields = position
      ? {
          lat: Math.round(position.latitude * 1000000) / 1000000,
          lon: Math.round(position.longitude * 1000000) / 1000000,
          ...(position.accuracy != null && { gps_accuracy: Math.round(position.accuracy * 100) / 100 }),
        }
      : {};
    await finalizeManualEntryCreate({
      ...baseCallLogData,
      ...gpsFields,
      gps_override_reason: reasonText,
      unverified_reason_code: code,
    });
    setPendingManualEntry(null);
  }, [pendingManualEntry, finalizeManualEntryCreate]);

  // Verification modal: user cancelled. Discard pending state and reset
  // the effect refs so they can retry by reloading or navigating back in.
  const handleVerificationCancel = useCallback(() => {
    setShowVerificationModal(false);
    setPendingManualEntry(null);
    manualEntryEffectRan.current = false;
    manualEntryCreatedRef.current = false;
    setError('Manual entry cancelled. Close this page or refresh to try again.');
  }, []);

  // Handle time conflict resolution
  const handleConflictTimesChanged = useCallback((newTimeIn: string, newTimeOut: string) => {
    setConflictTimeIn(newTimeIn);
    setConflictTimeOut(newTimeOut);
  }, []);

  const handleConflictRetry = useCallback(() => {
    // Reset the manual entry effect so it can run again with new times
    manualEntryEffectRan.current = false;
    manualEntryCreatedRef.current = false;
    setManualEntryCreated(false);
    setShowTimeConflictEditor(false);
    setTimeConflicts([]);

    // Update URL params with new times and navigate
    const newParams = new URLSearchParams(searchParams);
    newParams.set('timeIn', conflictTimeIn);
    newParams.set('timeOut', conflictTimeOut);
    navigate(`/store/${storeId}?${newParams.toString()}`, { replace: true });
  }, [conflictTimeIn, conflictTimeOut, navigate, storeId, searchParams]);

  const handleConflictCancel = useCallback(() => {
    setShowTimeConflictEditor(false);
    setTimeConflicts([]);
    // Navigate back to Time page
    navigate('/time');
  }, [navigate]);

  // Handle adding a note
  const handleAddNote = async (content: string) => {
    if (!store) return;

    if (!isOnline) {
      // Queue for later sync via the offline-first write queue (#1987).
      // writeOps.addStoreNote works fully offline: applyLocal records the
      // note in IDB and the sync engine drains it when connectivity returns.
      try {
        const { writeOps } = await import('../sync/writeOps');
        await writeOps.addStoreNote.apply({ storeId: store.id, content });
      } catch (enqErr) {
        console.warn('offline note enqueue failed (non-fatal):', enqErr);
      }
      // Add optimistically to local state
      setStoreNotes((prev) => [
        ...prev,
        {
          id: Date.now(),
          storeId: store.id,
          authorName: 'You (pending sync)',
          authorId: 0,
          content,
          createdAt: new Date().toISOString(),
          isPinned: false,
        },
      ]);
      return;
    }

    try {
      const newNote = await storesApi.addStoreNote(store.id, content);

      // Dual-write (Phase 1 of offline-first migration #1983/#1985).
      try {
        const { writeOps } = await import('../sync/writeOps');
        await writeOps.addStoreNote.apply({ storeId: store.id, content });
      } catch (enqErr) {
        console.warn('dual-write enqueue failed (non-fatal):', enqErr);
      }

      setStoreNotes((prev) => [
        ...prev,
        {
          id: newNote.id,
          storeId: newNote.storeId,
          authorName: newNote.authorName,
          authorId: newNote.authorId,
          content: newNote.content,
          createdAt: newNote.createdAt,
          isPinned: newNote.isPinned,
        },
      ]);
    } catch (err) {
      console.error('Failed to add note:', err);
      throw err;
    }
  };

  // Handle survey response changes — persists to IDB via useVisitTask.
  const handleSurveyResponseChange = (qid: number, responseText: string) => {
    // Clear task error when user answers a question (they may be fixing required fields)
    if (taskError) setTaskError(null);
    void saveSurveyResponse(qid, responseText);
  };

  // Document / photo upload handlers — state now lives in IDB via useVisitTask.
  const handleDocumentUpload = (file: UploadedDocument) => {
    void addDocument(file.file);
  };
  const handleDocumentRemove = (id: string) => {
    void removeDocument(id);
  };
  const handlePhotoUpload = (file: UploadedPhoto) => {
    void addPhoto(file.file);
  };
  const handlePhotoRemove = (id: string) => {
    void removePhoto(id);
  };

  // Called when GPS check-in is triggered - show odometer entry if tracking is active
  // gpsOverrideReason is passed when user checks in outside GPS tolerance
  const handleGPSCheckInComplete = async (gpsOverrideReason?: string) => {
    if (!store) return;

    // Checking in IS a punch: a rep who reached a store without clocking in is
    // working, so open a window for them rather than turning them away. No
    // drive leg — they are already here, and the drive they took goes in
    // through the retroactive form if they want it counted.
    if (dayData.status !== 'active' && !skipGpsCheck) {
      await startDay(undefined, { openDrive: false });
    }

    // Store the override reason for use in performCheckIn
    setPendingGpsOverrideReason(gpsOverrideReason);

    // If odometer tracking is active (has a current reading), show odometer entry first
    if (odometerData.currentReading !== null && store.lat && store.lng) {
      const calculated = calculateExpectedOdometer(store.lat, store.lng);
      setCalculatedOdometer(calculated);
      setShowOdometerEntry(true);
    } else {
      // No odometer tracking, proceed directly with check-in
      performCheckIn(gpsOverrideReason);
    }
  };

  // Called when odometer is entered at store arrival
  const handleOdometerSubmit = async (reading: number) => {
    if (!store) return;

    // Record odometer
    await recordStoreArrival(store.id, store.name, reading, calculatedOdometer || undefined);
    setShowOdometerEntry(false);

    // Now perform the actual check-in (with pending GPS override reason if any)
    await performCheckIn(pendingGpsOverrideReason);
    setPendingGpsOverrideReason(undefined);
  };

  // Called when drive details are confirmed (DriveConfirmation component)
  const handleDriveConfirmation = useCallback((driveData: DriveConfirmationData) => {
    setShowDriveConfirmation(false);

    // Convert DriveConfirmationData to DriveToStoreData format for useMultiTaskVisit
    const driveToStoreData = {
      driveStartTime: driveData.driveStartTime,
      driveEndTime: driveData.driveEndTime,
      startOdometer: driveData.startOdometer,
      endOdometer: driveData.endOdometer,
      storeCheckInTime: driveData.storeCheckInTime,
    };

    // Store the drive data in multi-task visit state
    setDriveData(driveToStoreData);

    // Move to task selection or task active phase
    setVisitPhase('task_active');
  }, [setDriveData]);

  // Skip drive confirmation (user was already at store, no drive to report)
  const handleSkipDriveConfirmation = useCallback(() => {
    setShowDriveConfirmation(false);
    setVisitPhase('task_active');
  }, []);

  // Perform the actual check-in logic
  // gpsOverrideReason: optional reason when checking in outside GPS tolerance
  const performCheckIn = useCallback(async (gpsOverrideReason?: string) => {
    if (!store) return;

    setIsCheckingIn(true);
    try {
      // Get projectId for disambiguation and day tracking.
      // Priority order (was inverted — caused the Publix 886 / Rob bug
      // where 3 check-ins landed on Pro Hair Care instead of Textured
      // Hair because store.projects[0] happened to be Pro Hair Care):
      //   1. The task the rep is actively working on, if any
      //   2. The projectId from the navigation URL — this is the rep's
      //      explicit intent ("I want to visit this store for THIS project")
      //   3. Fallback to the first project listed on the store (legacy)
      const projectId =
        multiTaskState.currentTask?.project_id
        || store.projectIdFromNav
        || store.projects[0]?.id
        || undefined;

      // Use day tracking context for check-in (local state)
      // Pass projectId so we can navigate back to the correct store
      // checkIntoStore returns closed drive data if a drive was in progress
      const closedDriveData = await checkIntoStore(store.id, store.name, projectId);

      // Use retroactive drive data if available (from starting day at store)
      // Otherwise use closed drive data from normal driving. Sanitize before
      // send (parity with CallDetail/SetDetail): drops a zero-length/inverted
      // time segment (#1991) and a lone odometer reading (the server requires
      // the pair together). An equal pair records as a zero-mile drive (#3618).
      const driveDataToUse = sanitizeDriveData(retroactiveDriveData || closedDriveData);

      // Clear retroactive drive data after use
      if (retroactiveDriveData) {
        setRetroactiveDriveData(null);
      }

      // Get GPS from day tracking context (captured during checkIntoStore)
      // Skip GPS for manual/retroactive entries
      const gps = (!isManualEntry && dayData.currentLocation)
        ? {
            lat: dayData.currentLocation.lat,
            lng: dayData.currentLocation.lng,
            // Carry accuracy so it persists on the CallLog (#2817 / diag #62).
            // currentLocation now populates it (useVisitDay.getCurrentLocation).
            accuracy: dayData.currentLocation.accuracy,
          }
        : undefined;
      const associateId = getAssociateId();
      if (!associateId) {
        console.error('No associate ID found - user may not be logged in properly');
        setError('Unable to check in: Please log out and log back in.');
        return;
      }
      // Queue-only check-in (#1987 Phase 3 — the sync queue is the SOLE write
      // path). Mirror the CallDetail/SetDetail pattern: enqueue the checkIn op
      // under a freshly-minted localId, then adopt that exact visit in place by
      // stamping the store row with `visitLocalIdFromNav`. The existing
      // in_progress auto-start effect adopts the session, and the reconcile
      // effect propagates the server calllogid back onto the day entry once the
      // POST syncs — identical infrastructure to a Call/Set-detail handoff, so
      // there's no direct apiCheckIn/createCallLog and no legacy addToQueue
      // offline fallback (the queue retries on its own until the POST lands).
      const { enqueueOp, newLocalId } = await import('../sync/writeOps');
      const localId = newLocalId();

      // For manual / retroactive entries use the manual date; otherwise today.
      const baseDate = manualDate || formatDateToLocal(new Date());
      const timeStr = new Date().toTimeString().slice(0, 5);

      // #2465: warn (don't hard-block) if this check-in overlaps an existing entry,
      // BEFORE queueing — a live conflict almost always means a stale/duplicate
      // session, the #2463 'failed to sync' cause. Online best-effort: any failure
      // (offline, timeline unavailable) skips the check and queues as before, so
      // offline check-in is never blocked. The server stays the source of truth.
      try {
        const [chH, chM] = timeStr.split(':').map(Number);
        const callStartMin = chH * 60 + chM;
        let winStart = callStartMin;
        if (driveDataToUse) {
          const ds = driveDataToUse.startTime;
          winStart = Math.min(winStart, ds.getHours() * 60 + ds.getMinutes());
        }
        const [{ callLogsApi }, { findTimelineOverlap }] = await Promise.all([
          import('../services/api'),
          import('../utils/timelineOverlap'),
        ]);
        const timeline = await callLogsApi.getDayTimeline(baseDate);
        const { message } = findTimelineOverlap(timeline.entries, winStart, callStartMin + 1);
        if (
          message &&
          !window.confirm(
            `Heads up — this check-in ${message}\n\n` +
              `If you're already working this store, go back and resume that task instead of starting a new one. Check in anyway?`,
          )
        ) {
          return; // rep chose to resolve it — don't queue a duplicate/overlapping entry
        }
      } catch {
        /* offline or timeline unavailable — skip the pre-check, queue as before */
      }

      await enqueueOp(
        'checkIn',
        {
          storeId: store.id,
          // projectId or fallback to 1 (server requires a number) — parity with
          // the prior apiCheckIn call.
          projectId: projectId || 1,
          associateId,
          storeName: store.name,
          // Skip GPS for manual/retroactive entries (parity with apiCheckIn).
          gps: gps
            ? { lat: gps.lat, lng: gps.lng, ...(gps.accuracy != null && { accuracy: gps.accuracy }) }
            : undefined,
          dteCall: baseDate,
          stCallTime: `${baseDate}T${timeStr}:00`,
          gpsOverrideReason,
          offScheduleReason: offScheduleReason || undefined,
          driveData: driveDataToUse
            ? {
                startTime: driveDataToUse.startTime.toISOString(),
                endTime: driveDataToUse.endTime.toISOString(),
                startOdometer: driveDataToUse.startOdometer,
                endOdometer: driveDataToUse.endOdometer,
              }
            : undefined,
        },
        localId,
      );

      // One visit's reason labels one visit. Left in the URL it would relabel
      // any later check-in from the same history entry (back button, refresh).
      if (offScheduleReason) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('offScheduleReason');
        setSearchParams(nextParams, { replace: true });
      }

      const checkInTime = manualDate
        ? new Date(baseDate + 'T' + timeStr + ':00').toISOString()
        : new Date().toISOString();
      // Adopt the just-enqueued visit in place. callLogId stays null until the
      // queue syncs (checkInOp.reconcile stamps it; the reconcile effect at the
      // visitLocalIdFromNav watcher then propagates it to the day entry for
      // end-of-day return-drive #1507).
      setStore((prev) =>
        prev
          ? { ...prev, callLogId: null, status: 'in_progress', checkInTime, visitLocalIdFromNav: localId }
          : null
      );
    } finally {
      setIsCheckingIn(false);
    }
  }, [store, checkIntoStore, dayData.currentLocation, isManualEntry, manualDate, getAssociateId, retroactiveDriveData, multiTaskState.currentTask]);

  // Note: Auto check-in feature removed - user must always click "Check In with GPS"
  // to ensure GPS verification happens. The autoCheckin URL param is now ignored.

  // Auto-open checkout summary when navigating with ?checkout=true (from Today page leave button)
  useEffect(() => {
    const shouldAutoCheckout = searchParams.get('checkout') === 'true';
    if (!shouldAutoCheckout) return;

    // Wait for store data to be loaded
    if (!store || isLoading || isDayLoading) return;

    // Check if we're checked in - either store.status is in_progress OR dayData shows we're in this store
    const isCheckedIn = store.status === 'in_progress' ||
      (dayData.currentStoreId === store.id && dayData.currentActivity === 'in_store');

    if (!isCheckedIn) {
      // Clear the param since we can't checkout
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('checkout');
      setSearchParams(newParams, { replace: true });
      return;
    }

    // Clear the checkout param from URL to prevent re-trigger on refresh
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('checkout');
    setSearchParams(newParams, { replace: true });

    // Open the checkout summary modal
    setShowCheckoutSummary(true);
  }, [store, isLoading, isDayLoading, dayData.currentStoreId, dayData.currentActivity, searchParams, setSearchParams]);

  // Auto-start task when store becomes in_progress (if coming from Sets/Routes page)
  useEffect(() => {
    if (!store) return;
    if (store.status !== 'in_progress') return;
    if (visitPhase !== 'pending') return;  // Only transition once
    // Wait for the visit state to hydrate from IDB before deciding what to do —
    // a re-entry remount that acts on an empty pre-hydration snapshot re-spawns
    // a completed task (#2016 / diag #44).
    if (!isVisitHydrated) return;

    // #2066: never adopt off a cold-load cache hydrate — only off server-confirmed
    // status. A stale cached 'in_progress' could otherwise drive decideArrivalAction
    // to 'start' and spawn a phantom task. Same-session returns set this true at
    // mount, so this only defers the new cold-load path until fetchStoreData runs.
    if (!serverConfirmed) return;

    // Capture GPS data at visit start if available
    const gpsData = dayData.currentLocation ? {
      lat: Math.round(dayData.currentLocation.lat * 1000000) / 1000000,
      lon: Math.round(dayData.currentLocation.lng * 1000000) / 1000000,
      accuracy: dayData.currentLocation.accuracy,
    } : undefined;

    // Check for closed drive data from day tracking (user was driving to this store)
    // This is captured when checkIntoStore closes the drive entry
    const closedDriveEntry = dayData.timeEntries.find(
      e => e.type === 'drive' && e.endTime && e.storeId === store.id
    );

    // Initialize multi-task visit with GPS data
    // Drive data is captured separately via DriveConfirmation component
    startMultiTaskVisit(store.id, undefined, gpsData);

    // If there's drive data from day tracking, show drive confirmation
    // Skip for manual entries which don't have real-time tracking
    if (!isManualEntry && closedDriveEntry && closedDriveEntry.endTime) {
      setPendingDriveData({
        startTime: closedDriveEntry.startTime,
        endTime: closedDriveEntry.endTime,
      });
      setShowDriveConfirmation(true);
      // Don't auto-start task - wait for drive confirmation
      return;
    }

    // Decide based on the REHYDRATED visit state — never re-spawn a task that's
    // already active / paused / completed in this visit (#2016 / diag #44). A
    // genuine first check-in has an empty snapshot, so this yields 'start' and
    // the existing behavior below is unchanged.
    const arrivalTarget =
      store.taskIdFromNav && store.taskTypeFromNav && store.projectIdFromNav
        ? { taskType: store.taskTypeFromNav, taskId: store.taskIdFromNav, projectId: store.projectIdFromNav }
        : { taskType: 'call' as const, taskId: 0, projectId: store.projectIdFromNav || store.projects[0]?.id || 0 };
    const arrivalAction = decideArrivalAction(arrivalTarget, {
      currentTask: multiTaskState.currentTask,
      pausedTasks: multiTaskState.pausedTasks.map((p) => p.task),
      completedTasks: multiTaskState.completedTasks,
    });
    const fetchTasksForPicker = async () => {
      try {
        const pid = store.projectIdFromNav || store.projects[0]?.id;
        const response = await storeTasksApi.getPendingTasks(store.id, manualDate || undefined, pid || undefined);
        setAvailableTasks({ sets: response.sets, calls: response.calls });
      } catch (err) {
        console.error('Failed to fetch available tasks:', err);
        setAvailableTasks({ sets: [], calls: [] });
      }
    };
    if (arrivalAction.kind === 'active') {
      // Already working this exact task (e.g. a nav-back remount) — show it,
      // don't restart it.
      setVisitPhase('task_active');
      void fetchTasksForPicker();
      return;
    }
    if (arrivalAction.kind === 'resume') {
      resumePausedTask(arrivalAction.taskKey);
      setVisitPhase('task_active');
      void fetchTasksForPicker();
      return;
    }
    if (arrivalAction.kind === 'completed') {
      // Returned to a store whose task for this project is already done — show
      // the picker (resume a paused task / start another / Leave Store) instead
      // of re-spawning the finished task with a blank survey (#2016 / diag #44).
      setVisitPhase('task_select');
      void fetchTasksForPicker();
      return;
    }

    // arrivalAction.kind === 'start' — genuinely new task: existing behavior.
    // If we have task info from navigation (from Sets or Routes page), auto-start that task
    if (store.taskIdFromNav && store.taskTypeFromNav && store.projectIdFromNav) {
      const taskFromNav: PendingTask = {
        task_type: store.taskTypeFromNav,
        task_id: store.taskIdFromNav,
        project_id: store.projectIdFromNav,
        project_description: store.projectDescFromNav || 'Task',
        client_name: store.clientNameFromNav || null,
        distributor_id: store.distributorIdFromNav || null,
        // The task carries its team verdict from birth (#3629) so the
        // non-lead deliverable suppression never races the background
        // pending-tasks fetch — the #3524 fix, extended to the set handoff.
        ...(store.teamProjectFromNav !== null && {
          is_team_project: store.teamProjectFromNav,
          is_current_user_lead: store.teamLeadFromNav === true,
        }),
      };
      // Pass callLogIdFromNav if this task already has a CallLog created at check-in
      // The bulk endpoint will UPDATE it instead of CREATE new.
      // #1987 Phase 3: a queue-only check-in hands off ?visitId= (no server id
      // yet) — adopt that exact session so the active id == the queue's id.
      startTask(
        taskFromNav,
        store.callLogIdFromNav || undefined,
        undefined,
        store.visitLocalIdFromNav ? { adoptUUID: store.visitLocalIdFromNav } : undefined
      );
      setVisitPhase('task_active');

      // Also fetch available tasks in background for when they finish this one
      const fetchTasksBackground = async () => {
        try {
          const projectId = store.projectIdFromNav || store.projects[0]?.id;
          const response = await storeTasksApi.getPendingTasks(store.id, manualDate || undefined, projectId || undefined);
          setAvailableTasks({ sets: response.sets, calls: response.calls });
        } catch (err) {
          console.error('Failed to fetch available tasks:', err);
          // Set empty arrays on error so UI still renders (shows "no tasks" state)
          setAvailableTasks({ sets: [], calls: [] });
        }
      };
      fetchTasksBackground();
    } else {
      // No specific task from nav - construct a synthetic task from store context
      // so the multi-task hook tracks this visit properly
      const projectId = store.projectIdFromNav || store.projects[0]?.id;
      if (projectId) {
        const syntheticTask: PendingTask = {
          task_type: 'call',
          task_id: 0,  // No specific route/set ID
          project_id: projectId,
          // Name must follow the SAME projectId — never positional (#1996).
          project_description: resolveProjectName(
            projectId,
            store.projects,
            store.projectIdFromNav,
            store.projectDescFromNav,
          ),
          client_name: store.clientNameFromNav || null,
          distributor_id: store.distributorIdFromNav || null,
        };
        startTask(
          syntheticTask,
          store.callLogId || store.callLogIdFromNav || undefined,
          undefined,
          store.visitLocalIdFromNav ? { adoptUUID: store.visitLocalIdFromNav } : undefined
        );
      }
      setVisitPhase('task_active');

      // Fetch available tasks for later use
      const fetchTasks = async () => {
        setIsLoadingTasks(true);
        try {
          const projectId2 = store.projectIdFromNav || store.projects[0]?.id;
          const response = await storeTasksApi.getPendingTasks(store.id, manualDate || undefined, projectId2 || undefined);
          setAvailableTasks({ sets: response.sets, calls: response.calls });
        } catch (err) {
          console.error('Failed to fetch available tasks:', err);
          // Set empty arrays on error so UI still renders (shows "no tasks" state)
          setAvailableTasks({ sets: [], calls: [] });
        } finally {
          setIsLoadingTasks(false);
        }
      };
      fetchTasks();
    }
  }, [store?.status, store?.id, visitPhase, isVisitHydrated, startMultiTaskVisit, startTask, resumePausedTask, multiTaskState.currentTask, multiTaskState.pausedTasks, multiTaskState.completedTasks, manualDate, dayData.currentLocation, dayData.timeEntries, isManualEntry, store?.taskIdFromNav, store?.taskTypeFromNav, store?.projectIdFromNav, store?.projectDescFromNav, store?.clientNameFromNav, store?.distributorIdFromNav, store?.callLogIdFromNav, store?.visitLocalIdFromNav, store?.teamProjectFromNav, store?.teamLeadFromNav, serverConfirmed]);

  // #2022 Phase-2 live-bug fix: the auto-start-on-check-in path builds the
  // active task from navigation params that omit the per-project workflow flags
  // (requires_order/inventory/oos), so currentTask starts flagless and the
  // order gate falls back to "order" even for an OOS-/inventory-only project.
  // The flags ride only on the pending-tasks payload (-> availableTasks). Once
  // that arrives, recover the active task's authoritative flags and merge them
  // onto currentTask + its IDB record. Self-healing: covers the start, resume,
  // and rehydrate paths uniformly (all can produce a flagless currentTask), and
  // is a no-op once the flags are current. The picker path (handleSelectTask)
  // already passes a flag-bearing PendingTask, so this is harmless there.
  // Sets + calls merged once — shared by the reconcile effect and the capture
  // button so neither re-spreads the arrays on every render.
  const allAvailableTasks = useMemo(
    () => [...availableTasks.sets, ...availableTasks.calls],
    [availableTasks],
  );

  // #2077 — required photo/document attachments for the active task's project,
  // recovered from the pending-tasks payload (same project matching as the #2022
  // workflow flags). Defaults to all-false when unmatched, so it never traps a rep.
  const requiredAttachments = useMemo(
    () => requiredAttachmentsForTask(multiTaskState.currentTask, allAvailableTasks),
    [multiTaskState.currentTask, allAvailableTasks],
  );

  // #2951 — is the current rep required to complete the shared deliverables
  // (survey/photos/documents) for this task? False for a NON-LEAD on a team set:
  // only the team lead must do them; the others just check in + record time. When
  // false the PWA FOLDS these sections away (collapsed, but still reachable, so a
  // teammate can finish the work if the lead's phone dies mid-set).
  const deliverablesRequired = useMemo(
    () => sharedDeliverablesRequiredForTask(multiTaskState.currentTask, allAvailableTasks),
    [multiTaskState.currentTask, allAvailableTasks],
  );

  // #2959 — the authoritative team config for this checkout, from the SET
  // assignment (never the rep's toggle). Scans every task touched this visit and
  // takes the first that maps to a team set. Drives the read-only "Team Project"
  // indicator AND gates the manager signature to the team lead.
  const liveTeamConfig = useMemo(() => {
    const visitTasks = [
      multiTaskState.currentTask,
      ...multiTaskState.pausedTasks.map((p) => p.task),
      ...multiTaskState.completedTasks,
    ].filter((t): t is NonNullable<typeof t> => !!t);
    for (const t of visitTasks) {
      const cfg = teamConfigForTask(t, allAvailableTasks);
      if (cfg) return cfg;
    }
    return null;
  }, [multiTaskState.currentTask, multiTaskState.pausedTasks, multiTaskState.completedTasks, allAvailableTasks]);

  // #3022 — LATCH the resolved team config for the duration of the visit. The
  // pending-tasks payload drops a set once it's completed (dtecomplete set), so
  // by checkout liveTeamConfig would go null and the team indicator/billing
  // teamproject would be lost. Capture it while the set is still pending/active,
  // and keep it until the visit is fresh again (no tasks). Team status is
  // deterministic from the set assignment — this only preserves what was
  // already resolved, it never invents a team config.
  const teamConfigLatch = useRef<ReturnType<typeof teamConfigForTask>>(null);
  useEffect(() => {
    const hasTasks = !!(
      multiTaskState.currentTask ||
      multiTaskState.pausedTasks.length ||
      multiTaskState.completedTasks.length
    );
    if (liveTeamConfig) teamConfigLatch.current = liveTeamConfig;
    else if (!hasTasks) teamConfigLatch.current = null; // fresh visit — drop any stale latch
  }, [liveTeamConfig, multiTaskState.currentTask, multiTaskState.pausedTasks, multiTaskState.completedTasks]);
  const checkoutTeamConfig = liveTeamConfig ?? teamConfigLatch.current;

  // #2092 — checkout signature gate, REQUIRED BY DEFAULT (only an explicit
  // requires_signature === false on the pending-tasks payload opts out). The
  // flag is project-level and hidden from the project UI for now.
  //
  // Derived from EVERY project touched in this visit — at Leave-Store time the
  // current task is usually null (paused or finished), so currentTask alone
  // would mis-gate. Strictest wins: if any visited project requires a
  // signature, checkout requires one. No task context at all -> required.
  //
  // #2959 — EXCEPTION: on a team set, the manager signature is the team LEAD's
  // job. A non-lead team member is not required to obtain it (it stays reachable
  // but optional/folded in CheckoutSummary), mirroring the deliverable gate.
  const requiresSignature = useMemo(() => {
    if (checkoutTeamConfig && !checkoutTeamConfig.isCurrentUserLead) return false;
    const visitTasks = [
      multiTaskState.currentTask,
      ...multiTaskState.pausedTasks.map((p) => p.task),
      ...multiTaskState.completedTasks,
    ].filter((t): t is NonNullable<typeof t> => !!t);
    if (visitTasks.length === 0) return true;
    return visitTasks.some((t) => signatureRequiredForTask(t, allAvailableTasks));
  }, [checkoutTeamConfig, multiTaskState.currentTask, multiTaskState.pausedTasks, multiTaskState.completedTasks, allAvailableTasks]);

  // #2077 — when the project requires an attachment, open the Photos/Documents
  // section by default so the mandatory section is immediately visible (it would
  // otherwise sit collapsed behind the #1102 "Optional" toggle).
  useEffect(() => {
    if (requiredAttachments.requiresPhotos || requiredAttachments.requiresDocuments) {
      setShowOptionalUploads(true);
    }
  }, [requiredAttachments]);

  useEffect(() => {
    const ct = multiTaskState.currentTask;
    if (!ct) return;
    const flags = findWorkflowFlagsForTask(ct, allAvailableTasks);
    if (!flags) return;
    // Also carry the distributor order rules (min/max) so they're persisted on
    // currentTask + its IDB record and survive an offline rehydrate. The hook's
    // own no-op guard makes this safe to call on every change (no render loop).
    const matched = allAvailableTasks.find((t) => t.project_id === ct.project_id);
    reconcileCurrentTaskWorkflow({
      ...flags,
      min_order_amount: matched?.min_order_amount ?? null,
      max_product_count: matched?.max_product_count ?? null,
    });
  }, [allAvailableTasks, multiTaskState.currentTask, reconcileCurrentTaskWorkflow]);

  // #1987 Phase 3: a queue-only check-in defers the server callLogId, so the
  // day-tracking store entry is no longer stamped with it at check-in. Once
  // the check-in syncs and checkInOp.reconcile stamps the visit row, propagate
  // that id to the current day entry so end-of-day return-drive
  // (Today.patchReturnDrive) can still target the CallLog (#1507). Bounded:
  // stops polling the moment it stamps.
  useEffect(() => {
    const localId = store?.visitLocalIdFromNav;
    if (!localId) return;
    let cancelled = false;
    let intervalId = 0;
    const check = async () => {
      const { getVisitByLocalId } = await import('../utils/visitDB');
      const row = await getVisitByLocalId(localId);
      const cid = row?.callLogId;
      if (typeof cid === 'number' && !cancelled) {
        setCurrentEntryCallLogId(cid);
        window.clearInterval(intervalId);
        window.removeEventListener('rdm:sync-enqueued', onEnq);
      }
    };
    const onEnq = () => void check();
    void check();
    intervalId = window.setInterval(check, 3000);
    window.addEventListener('rdm:sync-enqueued', onEnq);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('rdm:sync-enqueued', onEnq);
    };
  }, [store?.visitLocalIdFromNav, setCurrentEntryCallLogId]);

  // The ACTIVE task the rep is working right now. Leaving mid-work would drop it
  // — checkout only submits paused + completed sessions — so Leave Store is
  // blocked until the current task is paused or finished. Paused tasks do NOT
  // block: they're included in the checkout bulk submit (pausing is the normal
  // path to Leave Store), so blocking on them would deadlock checkout.
  const unfinishedTouchedTasks = multiTaskState.currentTask ? [multiTaskState.currentTask] : [];
  const hasUnfinishedTouchedTasks = unfinishedTouchedTasks.length > 0;

  // Open the checkout summary modal. Guarded: never open while a touched task
  // is still unfinished (the buttons are disabled too — this is the safety net).
  const handleLeaveStore = () => {
    if (hasUnfinishedTouchedTasks) return;
    setShowCheckoutSummary(true);
  };

  // Pause current task - stops the task timer but stays checked into store
  // User can then start another task, resume the paused task, or leave for a break
  const handlePauseVisit = () => {
    console.log('[StoreDetail] handlePauseVisit called');
    console.log('[StoreDetail] currentTask before pause:', multiTaskState.currentTask);
    console.log('[StoreDetail] visitPhase before pause:', visitPhase);

    setShowPauseVisitModal(false);

    // If there's an active task, pause it (save accumulated time)
    if (multiTaskState.currentTask) {
      const paused = pauseCurrentTask();
      console.log('[StoreDetail] pauseCurrentTask returned:', paused);
    }

    // Go to task selection phase where they can:
    // - Start a different task
    // - Resume the paused task
    // - Leave store (checkout)
    setVisitPhase('task_select');
    console.log('[StoreDetail] setVisitPhase to task_select');

    // Refresh available tasks
    fetchAvailableTasks();
    console.log('[StoreDetail] fetchAvailableTasks called');
  };

  // Cancel Visit state
  const [showCancelVisitModal, setShowCancelVisitModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonOther, setCancelReasonOther] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const handleCancelVisit = useCallback(async () => {
    const reason = cancelReason === 'Other' ? cancelReasonOther.trim() : cancelReason;
    if (!reason) return;

    setIsCancelling(true);
    try {
      // Void ALL call logs from this visit (check-in log + any completed task logs)
      const callLogIds = new Set<number>();
      const checkInLogId = store?.callLogId || store?.callLogIdFromNav;
      if (checkInLogId) callLogIds.add(checkInLogId);
      // Also void call logs from completed tasks
      for (const task of multiTaskState.completedTasks) {
        if (task.calllog_id) callLogIds.add(task.calllog_id);
      }
      // Void current task's call log if it has one
      if (multiTaskState.currentTaskCallLogId) callLogIds.add(multiTaskState.currentTaskCallLogId);

      const ids = Array.from(callLogIds);
      const results = await Promise.allSettled(ids.map((id) => callLogsApi.voidCallLog(id, reason)));

      // Only queue the ones the server did NOT already take (#3518).
      //
      // This used to enqueue a void for every id regardless, a dual-write left
      // from Phase 1 of the offline-first migration (#1983/#1985). When the
      // direct call had already succeeded the queued copy had nothing left to
      // do, and it could not even complete: it resolves its target through the
      // local visit row, so it failed as "Parent visit never synced" and sat in
      // Sync Issues asking the rep to fix a check-in that was fine. Cancelling a
      // visit reported an error for work that had gone through.
      //
      // A rejection here is the offline case, which is exactly what the queue is
      // for, so those still enqueue and drain later.
      const unsent = ids.filter((_, i) => results[i].status === 'rejected');
      if (unsent.length) {
        try {
          const { writeOps, resolveVisitLocalId } = await import('../sync/writeOps');
          for (const id of unsent) {
            const visitLocalId = await resolveVisitLocalId(id);
            if (visitLocalId) {
              await writeOps.voidCallLog.apply({ visitLocalId, voidReason: reason });
            }
          }
        } catch (enqErr) {
          console.warn('offline void enqueue failed (non-fatal):', enqErr);
        }
      }

      // Event stream (#1950): record the abandon BEFORE we tear down local
      // state so we still have the reason + voided CallLog list to attach.
      // One event per abandon, not per voided CallLog — those are payload.
      void emitEvent({
        event_type: 'store_visit_abandoned',
        event_at: new Date(),
        store_id: store?.id ?? null,
        calllog_id: checkInLogId ?? null,
        payload: {
          reason,
          voided_calllog_ids: Array.from(callLogIds),
          completed_task_count: multiTaskState.completedTasks.length,
          had_active_task: !!multiTaskState.currentTaskCallLogId,
        },
      });

      // Clean up DayTracking (remove store entry, don't start driving)
      if (store?.id) {
        cancelStoreVisit(dayData.currentStoreId || store.id);
      }

      // Reset multi-task state
      resetMultiTaskVisit();

      // Consolidated manual entry (#1863): the rep voided the CallLogs; drop
      // the pre-flight context so a refresh doesn't try to resume the visit.
      if (clock.isManual) {
        clearManualVisit();
      }

      // Navigate home
      navigate('/');
    } catch (err) {
      console.error('Failed to cancel visit:', err);
      // Still clean up locally even if API fails
      if (store?.id) {
        cancelStoreVisit(dayData.currentStoreId || store.id);
      }
      resetMultiTaskVisit();
      navigate('/');
    } finally {
      setIsCancelling(false);
    }
  }, [store, cancelReason, cancelReasonOther, cancelStoreVisit, dayData.currentStoreId, multiTaskState, resetMultiTaskVisit, navigate]);

  // Abandon the current task (discard time, return to task selection)
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const handleAbandonTask = useCallback(() => {
    // Capture the voided CallLog IDs and tell day-tracking to skip them
    // when patchReturnDrive runs at end-of-day (#1507). Without this the
    // return-drive odo is PATCHed onto a voided CallLog and the legacy
    // timesheet shows 0 miles instead of the actual daily total.
    const voidedCallLogIds = abandonCurrentTask();
    if (voidedCallLogIds.length > 0) {
      markCallLogsVoided(voidedCallLogIds);
      // If we just voided the CallLog the day-tracking store entry was
      // pointing at (because handleSelectTask updated it on multi-task
      // switch), and there's an EARLIER completed task at this same store,
      // rewind the entry to that task's CallLog so the store doesn't get
      // marked entirely voided. Otherwise getLastCompletedStoreEntry
      // skips the whole store and patchReturnDrive bails. #1507.
      const completed = multiTaskState.completedTasks;
      if (completed.length > 0) {
        const lastCompleted = completed[completed.length - 1];
        if (lastCompleted.calllog_id) {
          setCurrentEntryCallLogId(lastCompleted.calllog_id);
        }
      }
    }
    setShowAbandonConfirm(false);
    setVisitPhase('task_select');
    // Available tasks will be re-fetched by the existing useEffect that watches visitPhase
  }, [abandonCurrentTask, markCallLogsVoided, setCurrentEntryCallLogId, multiTaskState.completedTasks]);

  // Process checkout from the summary modal
  const handleCheckoutComplete = async (checkoutData: CheckoutData) => {
    // Use storeRef to get current callLogId - the `store` state captured in this closure
    // may be stale if auto-create effect updated it after this function was defined
    const currentCallLogId = storeRef.current?.callLogId;
    // #1987 Phase 3: queue ops key on the visit localId (always present),
    // not the server callLogId (null until the check-in syncs).
    const visitLocalId = getCurrentTaskLocalId();

    console.log('[Checkout] handleCheckoutComplete called');
    console.log('[Checkout] store:', store);
    console.log('[Checkout] store.callLogId (from state):', store?.callLogId);
    console.log('[Checkout] currentCallLogId (from ref):', currentCallLogId);
    console.log('[Checkout] checkoutData:', checkoutData);
    console.log('[Checkout] multiTaskState.completedTasks:', multiTaskState.completedTasks);
    console.log('[Checkout] multiTaskState.completedTasks.length:', multiTaskState.completedTasks.length);

    if (!store) return;

    setIsCheckingOut(true);
    setShowCheckoutSummary(false);

    try {
      // Upload documents and photos separately using correct endpoints.
      // Same priority order as performCheckIn — nav intent first.
      const projectId = store.projectIdFromNav || store.projects[0]?.id || 1;
      const today = formatDateToLocal(new Date());

      // In multi-task mode, each task's survey/uploads are submitted when the task finishes.
      // Only submit here for single-task visits (legacy flow).
      if (multiTaskState.completedTasks.length === 0) {
        // Upload documents (set forms, paperwork) -> TblDocumentdetails
        // Server's DetailDocumentUploadSerializer.validate filters by
        // legacystoreid, so pass legacyStoreId not the PK store.id.
        if (documentUploads.length > 0 && visitLocalId) {
          try {
            const docFiles = documentUploads.map(d => d.file);
            // Queue is the sole write path (#1987 Phase 3).
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.uploadDocument.apply({ visitLocalId, projectId, storeId: store.legacyStoreId, files: docFiles, date: today });
          } catch (err) {
            console.warn('Document upload failed', err);
          }
        }

        // Upload photos (shelf displays) -> TblPhotodetails
        if (photoUploads.length > 0 && visitLocalId) {
          try {
            const photoFiles = photoUploads.map(p => p.file);
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.uploadPhoto.apply({ visitLocalId, projectId, storeId: store.legacyStoreId, files: photoFiles, date: today });
          } catch (err) {
            console.warn('Photo upload failed', err);
          }
        }

        // Submit survey if responses exist and we have a valid surveyId
        if (surveyResponses.length > 0 && visitLocalId && surveyId) {
          try {
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.submitSurvey.apply({
              visitLocalId,
              surveyId,
              responses: surveyResponses.map((r) => ({ questionid: r.qid, responseText: r.responseText })),
            });
          } catch {
            console.log('Survey submission failed');
          }
        }
      }

      // Record the departure reading BEFORE the checkout punch: checkOutOfStore
      // opens the next drive leg and stamps it with the rep's current reading,
      // so this is what makes that drive start from the right odometer.
      if (checkoutData.departureOdometer != null && !isManualEntry) {
        try {
          await recordStoreDeparture(store.id, store.name, checkoutData.departureOdometer);
        } catch (err) {
          console.warn('[Checkout] departure odometer not recorded', err);
        }
      }

      // Use day tracking context for check-out
      await checkOutOfStore();

      // Submit the visit via the bulk endpoint whenever there are closed
      // sessions — completed OR paused. submitAllTasks already includes paused
      // records (each is a real CallLog row), so gating on completedTasks alone
      // dropped the common pause -> Leave Store flow: with a queue-only check-in
      // currentCallLogId is null, so it fell through to "NO CHECKOUT" and the
      // visit never got its edcalltime (#1987). The bulk path UPDATEs each
      // session's reconciled calllogid with the checkout time + signature.
      // Whichever branch runs, the visit's local id is what ties the checkout
      // event to the CallLog it closes — and it is not always the raw
      // `visitLocalId`, which is null on the paths that adopt a server row.
      let resolvedVisitLocalId: string | null = visitLocalId ?? null;
      let bulkCallLogIds: number[] = [];

      if (multiTaskState.completedTasks.length > 0 || multiTaskState.pausedTasks.length > 0) {
        // Submit all completed tasks via bulk API (includes drive data from multi-task state)
        const result = await submitAllTasks(
          checkoutData.managerName,
          checkoutData.signatureDataUrl || undefined,
          checkoutData.noSignatureReason || undefined,
          {
            teamproject: checkoutData.isTeamProject,
            teamleader: checkoutData.isTeamLeader,
            notes: checkoutData.visitNotes || undefined,
            // #2104 Split Time: partition the (possibly edited) visit span by the
            // rep's per-project allocation. Only when the gate is on AND the rep
            // engaged an allocation; otherwise null = default actual times.
            timeAllocation:
              splitTimeEnabled &&
              checkoutData.timeAllocation &&
              checkoutData.storeCheckInTime &&
              checkoutData.storeCheckOutTime
                ? {
                    visitStartISO: checkoutData.storeCheckInTime,
                    visitEndISO: checkoutData.storeCheckOutTime,
                    perProject: checkoutData.timeAllocation,
                  }
                : null,
          }
        );

        if (!result.ok) {
          // CRITICAL: Do NOT reset IDB — keep the active visit / completed tasks
          // intact so nothing is lost. Re-open the summary modal (#1500).
          // #3430: a TERMINAL failure (server 4xx validation) will keep
          // rejecting a blind retry, so don't tell the rep to "tap Complete
          // Visit again" forever — show the real reason and make the escape
          // (Cancel → keep working, fix later) explicit. A TRANSIENT failure
          // (5xx / deploy window / network) keeps the retry guidance.
          setError(
            result.terminal
              ? `This visit couldn't be submitted: ${result.message}. Your work is saved on this device — correct it and submit again, or tap Cancel to keep working and finish it later.`
              : 'Checkout submission failed. Your work is saved on this device. Tap Complete Visit again to retry.'
          );
          setShowCheckoutSummary(true);
          return; // skip resetMultiTaskVisit + navigate; finally clears isCheckingOut
        }

        // The rows the bulk submit just created. Without this the checkout event
        // could only carry a calllog_id the client already happened to hold —
        // null whenever the check-in had not synced — so the ledger could open a
        // visit and never close it, and no visit span was computable (#3537).
        bulkCallLogIds = result.callLogIds;

        // Confirmed server success — safe to clear local visit state
        resetMultiTaskVisit();
      } else if (currentCallLogId || visitLocalId) {
        console.log('[Checkout] BRANCH: Single-task checkout via writeOps.checkOut');
        console.log('[Checkout] isFullManualEntry:', isFullManualEntry);
        // Fallback: Single-task checkout (legacy flow) — now routed through
        // the sync queue (#1987) so it's offline-durable and replays in
        // order behind the check-in. The queue is the SOLE write path; the
        // old direct patchCallLog + addToQueue('check_out') backup is gone.
        //
        // The op keys on the visit localId (always present). If the
        // check-in already synced, currentCallLogId holds the server id —
        // adopt it so the op resolves the URL even when no local row carries
        // it yet (online single-task visits whose row predates the queue).
        const { writeOps, adoptServerCallLog } = await import('../sync/writeOps');
        const opVisitLocalId =
          visitLocalId ??
          (currentCallLogId ? await adoptServerCallLog(currentCallLogId) : null);
        // The checkout event below needs whatever id we actually resolved, not
        // the raw one: on this path `visitLocalId` is frequently null and the id
        // only exists after the adopt, which is why the event went out naming
        // nothing (#3537).
        resolvedVisitLocalId = opVisitLocalId;
        if (opVisitLocalId) {
          await writeOps.checkOut.apply({
            visitLocalId: opVisitLocalId,
            // For full manual entries, edcalltime is already set during
            // creation, so only PATCH the signature / team fields.
            ...(!isFullManualEntry && { edcalltime: new Date().toTimeString().slice(0, 5) }),
            managerName: checkoutData.managerName,
            signatureDataUrl: checkoutData.signatureDataUrl,
            noSignatureReason: checkoutData.noSignatureReason,
            teamproject: checkoutData.isTeamProject,
            teamleader: checkoutData.isTeamLeader,
            notes: checkoutData.visitNotes || undefined,
          });
          console.log('[Checkout] checkOut op enqueued');
        } else {
          console.log('[Checkout] Single-task checkout: no localId to key the op');
        }
      } else {
        console.log('[Checkout] BRANCH: NO CHECKOUT - no completed tasks and no callLogId!');
      }

      const checkOutTime = clock.departure();
      setStore((prev) =>
        prev ? { ...prev, status: 'completed', checkOutTime: checkOutTime.toISOString() } : null
      );

      // Event stream (#1950): one store_checked_out per visit, fired AFTER
      // the legacy CallLog/bulk_create writes succeeded. We pull the
      // calllog_id from the most-relevant source: the last completed
      // multi-task entry if present, else the single-task currentCallLogId.
      const lastCompletedCallLogId =
        bulkCallLogIds.length > 0
          ? bulkCallLogIds[bulkCallLogIds.length - 1]
          : multiTaskState.completedTasks.length > 0
            ? multiTaskState.completedTasks[multiTaskState.completedTasks.length - 1].calllog_id ?? null
            : currentCallLogId ?? null;
      void emitEvent({
        event_type: 'store_checked_out',
        event_at: checkOutTime,
        store_id: store.id,
        calllog_id: lastCompletedCallLogId,
        payload: {
          // The visit's local id, which is also the CallLog's idempotency key.
          // calllog_id above is null whenever the check-in has not synced yet,
          // and on prod that is most of the time — so without this the event
          // cannot be tied to the visit it closes, and the two accounts of that
          // visit can never be compared (#3537).
          local_id: resolvedVisitLocalId,
          task_count: multiTaskState.completedTasks.length,
          has_signature: !!checkoutData.signatureDataUrl,
          no_signature_reason: checkoutData.noSignatureReason ?? null,
          is_team_project: checkoutData.isTeamProject,
          is_team_leader: checkoutData.isTeamLeader,
          manual_entry: clock.isManual,
        },
      });

      // Reset visit phase
      setVisitPhase('pending');

      // Consolidated manual entry (#1863): clear the pre-flight context now
      // that the visit is fully checked out. The CallLog has its edcalltime +
      // signature on the server; nothing further is needed for this visit.
      if (clock.isManual) {
        clearManualVisit();
      }

      // Navigate back to Today page
      navigate('/');
    } finally {
      setIsCheckingOut(false);
    }
  };

  // ==========================================================================
  // Multi-Task Visit Handlers
  // ==========================================================================

  // Fetch available tasks for this store
  const fetchAvailableTasks = useCallback(async () => {
    if (!store) return;
    setIsLoadingTasks(true);
    try {
      const projectId = store.projectIdFromNav || store.projects[0]?.id;
      const response = await storeTasksApi.getPendingTasks(store.id, manualDate || undefined, projectId || undefined);
      console.log('[StoreDetail] fetchAvailableTasks response:', response);
      console.log('[StoreDetail] sets:', response.sets);
      console.log('[StoreDetail] calls:', response.calls);
      setAvailableTasks({ sets: response.sets, calls: response.calls });
    } catch (err) {
      console.error('Failed to fetch available tasks:', err);
      // Set empty arrays on error so UI still renders (shows "no tasks" state)
      setAvailableTasks({ sets: [], calls: [] });
    } finally {
      setIsLoadingTasks(false);
    }
  }, [store, manualDate]);

  // Handler: User selects a task to start
  const handleSelectTask = useCallback(async (task: PendingTask) => {
    if (!store) return;

    // #3028 — re-entering an already-completed task starts a NEW time entry
    // (a real, separate session — see checkout collapse). That's legitimate for
    // redoing work, but a stray tap shouldn't silently start a phantom timer, so
    // make it a conscious choice.
    if (getCompletedTaskIds().includes(task.task_id)) {
      const ok = window.confirm(
        'This task is already completed. Re-open it and start a NEW, separate time entry?'
      );
      if (!ok) return;
    }

    const associateId = getAssociateId();
    if (!associateId) {
      setError('Unable to start task: Please log out and log back in.');
      return;
    }

    // Reset survey-question metadata for the new task. Responses, photos,
    // and documents are now keyed to the task UUID in IDB via useVisitTask
    // and will clear automatically when currentTask changes.
    setSurveyQuestions([]);
    setSurveyId(null);

    try {
      // Create call log for the new task's project (same store, no drive/GPS — already at store)
      const callLogId = await apiCheckIn(
        store.id,
        task.project_id,
        associateId,
        undefined,       // no GPS — already captured at initial check-in
        undefined,       // no override date
        undefined,       // no GPS override reason
        undefined,       // no drive data — already at store
      );

      // Start the task with its call log ID
      startTask(task, callLogId || undefined);
      // Update the day-tracking store TimeEntry to point at the LATEST CallLog
      // for this store. Without this, patchReturnDrive at end-of-day would
      // target the first task's CallLog instead of the last task's, and the
      // legacy weekly timesheet would attribute drive-back to the wrong row.
      // (#1507 follow-up — multi-task drive-back attribution)
      if (callLogId) setCurrentEntryCallLogId(callLogId);
      setVisitPhase('task_active');
    } catch (err) {
      console.error('Failed to create call log for new task:', err);
      // Still start the task even if call log creation fails (offline scenario)
      // The bulk submit at checkout will create the call log
      startTask(task);
      setVisitPhase('task_active');
    }
  }, [store, getAssociateId, apiCheckIn, startTask, setCurrentEntryCallLogId, getCompletedTaskIds]);

  // Handler: User resumes a paused task. `taskKey` (when provided) selects
  // among multiple paused tasks (#1897); omitting it resumes the most
  // recent paused task (legacy single-paused-slot callers).
  const handleResumePausedTask = useCallback((taskKey?: string) => {
    resumePausedTask(taskKey);
    setVisitPhase('task_active');
  }, [resumePausedTask]);

  // Discard a paused session the rep opened by accident. Confirmed via a modal
  // because it drops that session's time; scoped to the one task.
  const [discardTarget, setDiscardTarget] = useState<{ key: string; name: string } | null>(null);
  const confirmDiscardPausedTask = useCallback(() => {
    if (discardTarget) discardPausedTask(discardTarget.key);
    setDiscardTarget(null);
  }, [discardTarget, discardPausedTask]);

  // Handler: User taps a row in the "Tasks at this store" list (#1955/#1996).
  //
  // The SiteTask already carries the authoritative identity (projectId +
  // display name) from deriveSiteTasks, so we start/switch IN PLACE using
  // that identity rather than navigating to a URL and rebuilding a synthetic
  // task from store.projects[0] (the positional lookup that crossed
  // id<->name in diag #37). Falls back to navigation only when no visit is
  // active yet — and even then carries projectDesc so the synthetic task is
  // labeled to match its projectId.
  const handleSelectSiteTask = useCallback(
    (t: SiteTask) => {
      // Already the active task — nothing to switch to.
      if (t.status === 'active') return;

      // Resume a paused task in place, matched by project identity.
      if (t.status === 'paused') {
        const match = multiTaskState.pausedTasks.find((p) => p.task.project_id === t.projectId);
        if (match) {
          handleResumePausedTask(
            `${match.task.task_type}:${match.task.task_id}:${match.task.project_id}`,
          );
          return;
        }
      }

      // Start (todo) or re-open (completed) a task. Identity comes straight
      // from the SiteTask — never a positional store.projects lookup.
      const task: PendingTask = {
        task_type: 'call',
        task_id: 0,
        project_id: t.projectId,
        project_description: t.projectName,
        client_name: null,
        distributor_id: null,
      };

      if (store && store.status === 'in_progress') {
        // Active visit: start/switch task in place (auto-pauses current).
        handleSelectTask(task);
      } else {
        // No active visit yet: navigate to start one, carrying the name so
        // the synthetic task is labeled correctly (#1996).
        navigate(
          `/store/${storeId}?projectId=${t.projectId}` +
            `&projectDesc=${encodeURIComponent(t.projectName)}&taskType=call`,
        );
      }
    },
    [multiTaskState.pausedTasks, handleResumePausedTask, handleSelectTask, store, storeId, navigate],
  );

  // Handler: User indicates no more tasks to complete
  const handleNoMoreTasks = useCallback(() => {
    // If no tasks completed yet, just proceed to checkout directly
    if (multiTaskState.completedTasks.length === 0) {
      setShowCheckoutSummary(true);
    } else {
      setVisitPhase('checkout');
      setShowCheckoutSummary(true);
    }
  }, [multiTaskState.completedTasks.length]);

  // Ref for scrolling to survey section when required fields are missing
  const surveyRef = useRef<HTMLDivElement>(null);

  // Handler: User finishes the current task
  const handleFinishTask = useCallback(async () => {
    // Bail if a finish is already in flight — double-tap guard (#1946).
    if (isFinishing) return;

    // #2951 — on a TEAM project only the team LEAD completes the shared project
    // deliverables (survey/photos/documents). A rep on a team project who is NOT
    // the lead just checks in (GPS) + records time, so we skip the deliverable
    // gates for them. Lead status is authoritative from the set assignment
    // (is_current_user_lead on the task), not the self-declared checkout toggle.
    // Defaults to REQUIRED for solo reps, the lead, and call tasks.
    const deliverablesRequired = sharedDeliverablesRequiredForTask(
      multiTaskState.currentTask,
      allAvailableTasks,
    );

    // Survey gate (#2688). Never let a survey that FAILED to load silently
    // un-gate checkout — the project may require a survey we couldn't fetch, so
    // block and force a retry rather than let the rep finish surveyless. Also
    // blocks on unanswered mandatory questions. A genuinely empty/no survey is
    // allowed (nothing to answer). Pure logic in evaluateSurveyGate for tests.
    const surveyGate = evaluateSurveyGate({
      surveyLoadError: surveyError,
      questions: surveyQuestions,
      responses: surveyResponses,
    });
    if (deliverablesRequired && surveyGate.blocked) {
      setTaskError(
        surveyGate.kind === 'load-error'
          ? "The survey for this task couldn't be loaded. Tap Retry in the Survey section, then finish."
          : `Please answer all required survey questions (${surveyGate.remaining} remaining) before finishing this task.`,
      );
      surveyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // #2077 — block finish until required photos/documents are attached. Survey
    // is gated separately above, so surveyRemaining=0 here. Expand the uploads
    // section so the rep can see what's missing.
    const attachmentGate = evaluateTaskCompletion(
      {
        requiresPhotos: requiredAttachments.requiresPhotos,
        requiresDocuments: requiredAttachments.requiresDocuments,
      },
      { photoCount: photoUploads.length, documentCount: documentUploads.length, surveyRemaining: 0 },
    );
    if (deliverablesRequired && !attachmentGate.allowed) {
      setShowOptionalUploads(true);
      setTaskError(`${attachmentGate.missing.join(' • ')} — attach before finishing this task.`);
      return;
    }

    setIsFinishing(true);
    try {

    // Submit current task's survey and uploads BEFORE finishing
    // (finishCurrentTask clears the callLogId, so we must submit first)
    // Wrap in top-level try/catch so finishCurrentTask ALWAYS runs
    try {
      // #1987 Phase 3: key uploads on the visit LOCALID, not the server
      // callLogId — which is null in the queue-only check-in flow (it arrives
      // only after the check-in syncs). Routing through the queue stores the
      // blobs/responses in visit_file_queue + visit_survey_responses and
      // resolves the calllogid lazily, so survey/photo/doc are never dropped.
      // activeCallLogId is kept for the (best-effort) event stream only.
      const visitLocalId = getCurrentTaskLocalId();
      const activeCallLogId = multiTaskState.currentTaskCallLogId || store?.callLogId;
      const activeProjectId = multiTaskState.currentTask?.project_id || store?.projectIdFromNav || store?.projects?.[0]?.id;

      if (visitLocalId && activeProjectId) {
        const today = formatDateToLocal(new Date());

        // Upload documents for this task. Server expects legacystoreid, not PK.
        if (documentUploads.length > 0) {
          try {
            const docFiles = documentUploads.map(d => d.file);
            // Queue is the sole write path (#1987 Phase 3): enqueue the blobs
            // keyed on the visit localId; the sync engine uploads them once the
            // check-in's calllogid lands. Works even though activeCallLogId is
            // null in the queue-only flow.
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.uploadDocument.apply({ visitLocalId, projectId: activeProjectId, storeId: store?.legacyStoreId || 0, files: docFiles, date: today });
            // One event per batch of documents — payload carries the
            // count + sizes so the event log captures the upload without
            // the bytes themselves. (#1950)
            void emitEvent({
              event_type: 'document_uploaded',
              event_at: new Date(),
              store_id: store?.id ?? null,
              project_id: activeProjectId,
              calllog_id: activeCallLogId,
              payload: {
                file_count: docFiles.length,
                file_names: docFiles.map((f) => f.name),
                total_bytes: docFiles.reduce((sum, f) => sum + (f.size || 0), 0),
              },
            });
          } catch (err) {
            console.warn('Document upload failed during task finish', err);
          }
        }

        // Upload photos for this task. Server expects legacystoreid, not PK.
        if (photoUploads.length > 0) {
          try {
            const photoFiles = photoUploads.map(p => p.file);
            // Queue is the sole write path (#1987 Phase 3) — see uploadDocument above.
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.uploadPhoto.apply({ visitLocalId, projectId: activeProjectId, storeId: store?.legacyStoreId || 0, files: photoFiles, date: today });
            void emitEvent({
              event_type: 'photo_uploaded',
              event_at: new Date(),
              store_id: store?.id ?? null,
              project_id: activeProjectId,
              calllog_id: activeCallLogId,
              payload: {
                file_count: photoFiles.length,
                file_names: photoFiles.map((f) => f.name),
                total_bytes: photoFiles.reduce((sum, f) => sum + (f.size || 0), 0),
              },
            });
          } catch (err) {
            console.warn('Photo upload failed during task finish', err);
          }
        }

        // Submit survey for this task. Sends this task's IDB-backed responses
        // scoped to this task's CallLog. (#1471 follow-up #9, #1948)
        if (surveyResponses.length > 0 && surveyId) {
          try {
            // Queue is the sole write path (#1987 Phase 3): keyed on the visit
            // localId so it submits even before the check-in's calllogid lands.
            const { writeOps } = await import('../sync/writeOps');
            await writeOps.submitSurvey.apply({
              visitLocalId,
              surveyId,
              responses: surveyResponses.map((r) => ({ questionid: r.qid, responseText: r.responseText })),
            });
            // One event per submission — no PII in payload, just metadata
            // about which survey + how many answers. The actual responses
            // live in TblSurveyResponse server-side. (#1950)
            void emitEvent({
              event_type: 'survey_submitted',
              event_at: new Date(),
              store_id: store?.id ?? null,
              project_id: activeProjectId,
              calllog_id: activeCallLogId,
              payload: {
                survey_id: surveyId,
                response_count: surveyResponses.length,
                qids: surveyResponses.map((r) => r.qid),
              },
            });
          } catch (err) {
            // Surface the server's error body so 400s don't silently drop data (#1511)
            const apiErr = err as { status?: number; data?: unknown; message?: string };
            console.error('[survey-submit] failed', {
              status: apiErr.status,
              data: apiErr.data,
              message: apiErr.message,
              calllogid: activeCallLogId,
              surveyid: surveyId,
              responseCount: surveyResponses.length,
            });
          }
        } else if (surveyResponses.length > 0 && !surveyId) {
          // Guard fired — surveyId wasn't loaded yet when user clicked Finish.
          // Surface this to console so we can catch it in the next run. (#1511)
          console.error('[survey-submit] skipped — surveyId is null despite answered questions', {
            calllogid: activeCallLogId,
            responseCount: surveyResponses.length,
            projectId: activeProjectId,
          });
        }

        // Survey responses persist in IDB keyed by task UUID; they're cleared
        // automatically when the task transitions to 'completed'. No
        // sessionStorage cleanup needed anymore (#1469 band-aid removed).
      }
    } catch (err) {
      console.error('Error during pre-finish submissions, continuing with task finish:', err);
    }

    // ALWAYS finish the task regardless of submission errors.
    // Manual mode (#1863): pass the rep's pre-entered time-out as the task end
    // so the recorded duration matches what the rep entered. Live mode passes
    // nothing and finishCurrentTask uses new Date() as today.
    const completed = finishCurrentTask(clock.isManual ? clock.taskEnd() : undefined);
    if (completed) {
      setLastCompletedTask(completed);
      setVisitPhase('task_complete');
    }
    } finally {
      setIsFinishing(false);
    }
  }, [isFinishing, finishCurrentTask, clock, multiTaskState.currentTaskCallLogId, multiTaskState.currentTask?.project_id, store, documentUploads, photoUploads, surveyResponses, surveyQuestions, surveyId, requiredAttachments]);

  // Handler: Start another task after completing one
  const handleStartAnotherTask = useCallback(() => {
    setVisitPhase('task_select');
    setLastCompletedTask(null);
    fetchAvailableTasks();
  }, [fetchAvailableTasks]);

  // Calculate remaining tasks
  const getRemainingTaskCount = useCallback((): number => {
    if (!availableTasks) return 0;
    // #2125: same inclusion rule as the Other-tasks panel and the picker —
    // exclude completed AND the currently-active task AND depleted calls.
    // The three surfaces used to each have their own filter and disagreed
    // on-screen at the same moment (diag #52: top said +4, panel said 3).
    const excludeIds = [...getCompletedTaskIds(), multiTaskState.currentTask?.task_id ?? -1];
    return (
      startableSets(availableTasks.sets, excludeIds).length +
      startableCalls(availableTasks.calls, excludeIds).length
    );
  }, [availableTasks, getCompletedTaskIds, multiTaskState.currentTask?.task_id]);

  // ==========================================================================
  // Start Day Flow Handlers (inline flow when user hasn't started their day)
  // When starting from a store, we use RetroactiveStartDay to capture:
  // - Departure time (when they left home)
  // - Starting odometer (at home)
  // - Arrival odometer (at store)
  // ==========================================================================

  // Show retroactive start day form directly (skip location prompt - they're at the store)
  const handleStartDayClick = () => {
    setShowStartDayFlow(true);
    // Skip location prompt - go directly to retroactive start
    // (location permission was for GPS tracking during driving, but they're already at the store)
  };

  // Called when location permission is resolved (kept for compatibility but not used in retroactive flow)
  const handleLocationReady = (hasLocation: boolean) => {
    setHasLocationPermission(hasLocation);
    setShowLocationPrompt(false);
  };

  // Called when retroactive start day data is submitted
  const handleRetroactiveStartDay = async (data: RetroactiveStartData) => {
    // Record starting odometer with retroactive timestamp
    if (data.startOdometer !== null) {
      await startDayOdometer(data.startOdometer, data.departureTime);
    }

    // Start day with retroactive start time
    await startDay(data.departureTime);

    // Record arrival odometer if provided
    if (data.arrivalOdometer !== null && store) {
      await recordStoreArrival(store.id, store.name, data.arrivalOdometer);
    }

    // Store drive data to pass to check-in. #3019 — close the drive at the
    // ACTUAL arrival time, not "now" (the backfill moment), so a late check-in
    // doesn't inflate the drive segment.
    const arrival = data.arrivalTime;
    const durationMinutes = Math.round((arrival.getTime() - data.departureTime.getTime()) / 60000);
    setRetroactiveDriveData({
      startTime: data.departureTime,
      endTime: arrival,
      durationMinutes,
      startOdometer: data.startOdometer ?? undefined,
      endOdometer: data.arrivalOdometer ?? undefined,
    });

    // Close the flow
    setShowStartDayFlow(false);
    setHasLocationPermission(null);

    // DON'T start driving - they're at the store!
    // The drive data is captured above and will be passed to check-in
  };

  // Skip mileage tracking and start with just time
  const handleSkipOdometer = async () => {
    // They're standing in the store, so there is no drive to open — and
    // startDay() with no arguments opens one, which is what the comment here
    // used to deny while the code did it anyway.
    await startDay(undefined, { openDrive: false });
    setShowStartDayFlow(false);
    setHasLocationPermission(null);
  };

  // Cancel the start day flow
  const handleCancelStartDay = () => {
    setShowLocationPrompt(false);
    setShowStartDayFlow(false);
    setHasLocationPermission(null);
    setRetroactiveDriveData(null);
  };

  const openDirections = () => {
    setShowMapModal(true);
  };

  const callStore = () => {
    if (!store) return;
    window.location.href = `tel:${store.phone}`;
  };

  // Manual-entry create failed (timeout or network). Show a retry screen
  // instead of letting the page sit on an indefinite spinner. #1850
  if (manualEntryError && isFullManualEntry && !manualEntryCreated && !isCreatingManualEntry) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-card text-center">
          <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 4h.01M4.93 19h14.14a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.19 16a2 2 0 001.74 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-800">Couldn't create your entry</h2>
          <p className="mt-2 text-sm text-slate-600">{manualEntryError}</p>
          <div className="mt-5 flex gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex-1 rounded-lg border border-slate-300 bg-white py-2.5 font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={retryManualEntryCreate}
              className="flex-1 rounded-lg bg-primary-600 py-2.5 font-medium text-white hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state — only blank to the full-screen spinner on the FIRST load
  // (no store yet). Once we have store data (incl. a cached hydrate on remount),
  // background refreshes update in place instead of flashing this spinner, which
  // fixes both the lingering "Loading store details…" and the flash-then-white
  // bug on returning from the scanner / after check-in.
  if ((isLoading && !store) || isCreatingManualEntry) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <p className="mt-3 text-slate-500">
            {isCreatingManualEntry ? 'Creating call log entry...' : 'Loading store details...'}
          </p>
        </div>
      </div>
    );
  }

  // Time conflict state - show conflict resolution UI
  if (showTimeConflictEditor && timeConflicts.length > 0) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <button
          onClick={handleConflictCancel}
          className="mb-4 flex items-center gap-2 text-slate-600"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <TimeConflictEditor
          errorMessage=""
          conflictingEntries={timeConflicts}
          proposedTimeIn={conflictTimeIn}
          proposedTimeOut={conflictTimeOut}
          proposedDate={manualDate || ''}
          onTimesChanged={handleConflictTimesChanged}
          onRetry={handleConflictRetry}
          onCancel={handleConflictCancel}
        />
      </div>
    );
  }

  // Page-level fatal load error: only when the store could not be loaded at
  // all. Operational errors (checkout failure, task start failure, etc.) DO
  // NOT route through here — they surface as an inline banner inside the page
  // (see below) so the rep can retry without losing the page. Previously every
  // setError call kicked the user to this "Unable to load store" screen even
  // for transient checkout 5xx, which masked the real message and prevented
  // retry from the modal. #1853-sibling
  if (!store) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center gap-2 text-slate-600"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="rounded-xl bg-red-50 p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-3 font-semibold text-red-800">Unable to load store</h3>
          <p className="mt-1 text-sm text-red-600">{error || 'Store not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 pb-4 pt-4 text-white safe-area-inset-top">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-white/10 p-2 transition-colors hover:bg-white/20"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-lg font-bold">{store.name}</h1>
              {store.hasOpenOrders && (
                <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium">
                  Open Orders
                </span>
              )}
            </div>
            <p className="text-sm text-primary-200">{store.address}</p>
            {(() => {
              // Show the ACTIVE task's project (id + REAL name), not a positional
              // store.projects[0] lookup that surfaced "#1102 #1102" (#2012).
              const headerPid = multiTaskState.currentTask?.project_id || store.projects[0]?.id || store.projectIdFromNav;
              if (!headerPid) return null;
              const headerName = projectDisplayName(
                headerPid,
                multiTaskState.currentTask?.project_description || store.projectDescFromNav,
              );
              return (
                <p className="text-sm text-primary-200">
                  <span className="font-mono">#{headerPid}</span>
                  {headerName && headerName !== `#${headerPid}` && headerName !== 'Project' && (
                    <span> {headerName}</span>
                  )}
                </p>
              );
            })()}
          </div>
          {/* #2199 (diag #55): surface the sync-status chip here so the rep can
              see pending/failed sync state without exiting the active task. */}
          <ApiStatusIndicator />
          {/* Cancel Visit button - only when checked in */}
          {store.status === 'in_progress' && (
            <button
              onClick={() => setShowCancelVisitModal(true)}
              className="rounded-lg bg-white/10 p-2 transition-colors hover:bg-red-500/30"
              title="Cancel Visit"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {/* Task Timer Stack - shows active task timer and paused tasks */}
      {/* For full manual entries, show static time info instead of running timer */}
      {store.status === 'in_progress' && isFullManualEntry && manualEntryCreated && (
        <div className="px-4 pb-3">
          <div className="bg-amber-600 rounded-lg px-3 py-2 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="flex items-center gap-1 text-amber-200 text-xs font-medium uppercase">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Manual Entry
                </div>
              </div>
              <div className="font-mono text-lg font-bold">
                {manualTimeIn} - {manualTimeOut || '??:??'}
              </div>
            </div>
            <p className="text-sm text-amber-100 mt-0.5">
              {manualDate && new Date(manualDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {manualDriveStartOdo && manualDriveEndOdo && (
                <span className="ml-2">
                  | Mileage: {manualDriveEndOdo} - {manualDriveStartOdo} = {parseInt(manualDriveEndOdo) - parseInt(manualDriveStartOdo)} mi
                </span>
              )}
            </p>
          </div>
        </div>
      )}
      {/* Consolidated manual entry (#1863): static time block in place of the
          running timer. The rep is recording past or pre-scheduled work, so a
          live "elapsed" counter (now - startTime) is meaningless — it would
          show 24+ hours for a retroactive entry from yesterday. Mirror the
          legacy isFullManualEntry static block. */}
      {store.status === 'in_progress' && manualVisit && (
        <div className="px-4 pb-3">
          <div className="bg-amber-600 rounded-lg px-3 py-2 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="flex items-center gap-1 text-amber-200 text-xs font-medium uppercase">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {manualVisit.useCase === 'live-takeover' ? 'Live Takeover' : 'Manual Entry'}
                </div>
                {multiTaskState.currentTask && (
                  <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                    multiTaskState.currentTask.task_type === 'set'
                      ? 'bg-purple-500/30 text-purple-100'
                      : 'bg-blue-500/30 text-blue-100'
                  }`}>
                    {multiTaskState.currentTask.task_type === 'set' ? 'Set' : 'Call'}
                  </span>
                )}
              </div>
              <div className="font-mono text-lg font-bold">
                {clock.toHHMM(new Date(manualVisit.timeIn))} - {clock.toHHMM(new Date(manualVisit.timeOut))}
              </div>
            </div>
            <p className="text-sm text-amber-100 mt-0.5 truncate">
              {new Date(manualVisit.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {' · '}
              {(manualVisit.driveEndOdometer - manualVisit.driveStartOdometer)} mi
              {multiTaskState.currentTask && (
                <span className="ml-2 opacity-80">| {multiTaskState.currentTask.project_description}</span>
              )}
            </p>
          </div>
        </div>
      )}
      {store.status === 'in_progress' && !isFullManualEntry && !manualVisit && (multiTaskState.currentTask || multiTaskState.pausedTasks.length > 0) && (
        <div className="px-4 pb-3 space-y-2">
          {/* Active Task - green background with running timer. Tappable
              (#2126): jumps to the Current Task card — the banner is the most
              prominent affordance on screen and reps expect it to react. */}
          {multiTaskState.currentTask && multiTaskState.currentTaskStartTime && (
            <button
              type="button"
              onClick={() => document.getElementById('current-task-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="block w-full text-left bg-green-600 rounded-lg px-3 py-2 text-white transition-colors active:bg-green-700"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="flex items-center gap-1 text-green-200 text-xs font-medium uppercase">
                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    Active
                  </div>
                  <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                    multiTaskState.currentTask.task_type === 'set'
                      ? 'bg-purple-500/30 text-purple-100'
                      : 'bg-blue-500/30 text-blue-100'
                  }`}>
                    {multiTaskState.currentTask.task_type === 'set' ? 'Set' : 'Call'}
                  </span>
                </div>
                <div className="font-mono text-lg font-bold">
                  <Timer startTime={multiTaskState.currentTaskStartTime.toISOString()} />
                </div>
              </div>
              <p className="text-sm text-green-100 truncate mt-0.5">
                <span className="font-mono opacity-90">#{multiTaskState.currentTask.project_id}</span>{' '}
                {projectDisplayName(multiTaskState.currentTask.project_id, multiTaskState.currentTask.project_description)}
              </p>
            </button>
          )}
          {/* Paused Tasks - one amber chip per paused task (#1897). Tappable
              (#2126): resumes that task — startTask auto-pauses whatever is
              currently active, so this is a one-tap switch, same path as the
              paused pill in the Tasks-at-this-store list. */}
          {multiTaskState.pausedTasks.map((paused) => {
            const pausedKey = `${paused.task.task_type}:${paused.task.task_id}:${paused.task.project_id}`;
            const pausedName = projectDisplayName(paused.task.project_id, paused.task.project_description);
            return (
              <div key={pausedKey} className="flex items-stretch gap-px overflow-hidden rounded-lg bg-amber-600 text-white">
                <button
                  type="button"
                  onClick={() => handleResumePausedTask(pausedKey)}
                  className="min-w-0 flex-1 px-3 py-2 text-left transition-colors active:bg-amber-700"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="flex items-center gap-1 text-amber-200 text-xs font-medium uppercase">
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M10 9v6m4-6v6" />
                        </svg>
                        Paused
                      </div>
                      <span className={`flex-shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                        paused.task.task_type === 'set'
                          ? 'bg-purple-500/30 text-purple-100'
                          : 'bg-blue-500/30 text-blue-100'
                      }`}>
                        {paused.task.task_type === 'set' ? 'Set' : 'Call'}
                      </span>
                    </div>
                    <div className="font-mono text-lg font-bold">
                      {paused.accumulatedMinutes}m
                    </div>
                  </div>
                  <p className="text-sm text-amber-100 truncate mt-0.5">
                    <span className="font-mono opacity-90">#{paused.task.project_id}</span>{' '}
                    {pausedName}
                  </p>
                </button>
                <button
                  type="button"
                  aria-label="Discard paused session"
                  onClick={() => setDiscardTarget({ key: pausedKey, name: pausedName })}
                  className="flex flex-shrink-0 items-center justify-center px-3 text-amber-200 transition-colors hover:bg-amber-700 hover:text-white"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M4 7h16" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Content */}
      <main className="p-4">
        {/* Inline operational error banner — shows transient errors like
            checkout submission failures without kicking the rep off the
            page. Page-level fatal load errors are handled above. */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="flex-1 text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="rounded p-1 text-red-500 transition-colors hover:bg-red-100"
                aria-label="Dismiss error"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Consolidated manual entry banner (#1863) — persistent throughout
            the visit so the rep always knows they're in manual mode. Only
            fires for the new pre-flight path; legacy path renders the older
            isManualEntry banner below. */}
        {manualVisit && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800">
                  {manualVisit.useCase === 'live-takeover' ? 'Live takeover' : 'Recording past visit'}
                </h4>
                <p className="mt-1 text-sm text-amber-700">
                  {new Date(manualVisit.date + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                  })}
                  {' · '}
                  <span className="font-medium">{clock.toHHMM(new Date(manualVisit.timeIn))}</span>
                  {' – '}
                  <span className="font-medium">{clock.toHHMM(new Date(manualVisit.timeOut))}</span>
                  {' · '}
                  {(manualVisit.driveEndOdometer - manualVisit.driveStartOdometer)} mi
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Manual Entry Banner */}
        {isManualEntry && (
          <div className="mb-4 rounded-xl bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-amber-800">Manual Entry Mode</h4>
                <p className="mt-1 text-sm text-amber-700">
                  Recording {isFullManualEntry ? 'call/set' : 'call'} for{' '}
                  <span className="font-medium">
                    {new Date(manualDate + 'T00:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                  .
                </p>
                {isFullManualEntry && (
                  <div className="mt-2 text-xs text-amber-700 space-y-0.5">
                    <p><span className="font-medium">Time:</span> {manualTimeIn} - {manualTimeOut}</p>
                    {manualDriveStartOdo && manualDriveEndOdo && (
                      <p><span className="font-medium">Mileage:</span> {manualDriveStartOdo} to {manualDriveEndOdo} ({parseInt(manualDriveEndOdo) - parseInt(manualDriveStartOdo)} mi)</p>
                    )}
                    {manualTeamProject && (
                      <p><span className="font-medium">Team Project:</span> Yes{manualTeamLeader ? ' (Leader)' : ''}</p>
                    )}
                    {manualNotes && (
                      <p><span className="font-medium">Notes:</span> {manualNotes.length > 50 ? manualNotes.substring(0, 50) + '...' : manualNotes}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Store Info Card */}
        <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
          <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            Store Information
          </h3>
          <p className="text-slate-800">{store.address}</p>
          <p className="text-slate-600">{store.city}</p>
          {store.phone && <p className="mt-1 text-slate-500">{store.phone}</p>}

          <div className="mt-4 flex gap-3">
            <button
              onClick={openDirections}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Directions
            </button>
            {store.phone && (
              <button
                onClick={callStore}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call
              </button>
            )}
          </div>
        </div>

        {/* Tasks at this store (#1955): one row per project, status derived
            from schedule + today's CallLogs + local sessions. Tapping
            starts/switches/resumes that task in place via
            handleSelectSiteTask, carrying its projectId + name (#1996). */}
        {siteTasks.length > 0 && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
              Tasks at this store
            </h3>
            <div className="space-y-1">
              {siteTasks.map((t) => (
                <button
                  key={t.projectId}
                  onClick={() => handleSelectSiteTask(t)}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm transition-colors hover:bg-slate-50"
                >
                  <span className="flex items-center gap-2 truncate text-slate-800">
                    <span aria-hidden>
                      {t.status === 'active' ? '●' : t.status === 'paused' ? '⏸' : t.status === 'completed' ? '✓' : '○'}
                    </span>
                    <span className="truncate">{`#${t.projectId} ${t.projectName}`}</span>
                  </span>
                  <span className="text-xs uppercase tracking-wide text-slate-500">{t.status}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Store Notes */}
        <div className="mb-4">
          <StoreNotes
            storeId={store.id}
            notes={storeNotes}
            onAddNote={handleAddNote}
            isLoading={isLoadingNotes}
            disabled={false}
          />
        </div>

        {/* Drive Confirmation Phase - shown when arriving at store */}
        {showDriveConfirmation && (
          <div className="mb-4">
            <DriveConfirmation
              estimatedDriveStart={pendingDriveData?.startTime || null}
              estimatedDriveEnd={pendingDriveData?.endTime || null}
              storeAddress={`${store.address}, ${store.city}`}
              storeName={store.name}
              onConfirm={handleDriveConfirmation}
              onCancel={() => setShowDriveConfirmation(false)}
              onSkipDrive={handleSkipDriveConfirmation}
              isLoading={isCheckingIn}
            />
          </div>
        )}

        {/* #2087 (diag #48): cold-load resume window — the visit is known
            in_progress (cache paint) but task adoption waits on server
            confirmation + IDB hydration, so none of the task sections below
            render yet. Without this the area under the header is blank for
            the whole load barrier. */}
        {store.status === 'in_progress' && visitPhase === 'pending' && !showDriveConfirmation && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card" data-testid="visit-resume-skeleton">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
              Resuming your visit...
            </div>
            <div className="mt-3 space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200"></div>
              <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200"></div>
              <div className="h-20 animate-pulse rounded-lg bg-slate-100"></div>
            </div>
          </div>
        )}

        {/* Multi-Task UI: Task Selection Phase */}
        {store.status === 'in_progress' && visitPhase === 'task_select' && (
          <div className="mb-4">
            <TaskSelector
              storeId={store.id}
              storeName={store.name}
              projectId={store.projectIdFromNav || store.projects[0]?.id}
              onSelectTask={handleSelectTask}
              onNoMoreTasks={handleNoMoreTasks}
              onResumePausedTask={multiTaskState.pausedTasks.length > 0 ? handleResumePausedTask : undefined}
              excludeTaskIds={getCompletedTaskIds()}
              pausedTasks={multiTaskState.pausedTasks}
              isLoading={isLoadingTasks}
            />
          </div>
        )}

        {/* Multi-Task UI: Task Completion Prompt */}
        {store.status === 'in_progress' && visitPhase === 'task_complete' && lastCompletedTask && (
          <div className="mb-4">
            <TaskCompletionPrompt
              completedTask={lastCompletedTask}
              remainingTaskCount={getRemainingTaskCount()}
              onStartAnotherTask={handleStartAnotherTask}
              onLeaveStore={handleLeaveStore}
              isLoading={isCheckingOut}
              leaveButtonLabel={clock.isManual ? `Confirm Departure (${clock.toHHMM(clock.departure())})` : undefined}
              leaveBlockedReason={
                hasUnfinishedTouchedTasks
                  ? unfinishedTouchedTasks.map((t) => t.project_description).join(', ')
                  : undefined
              }
            />
          </div>
        )}

        {/* Leave-Store reminder banner (#1945 Phase 2c). Shown whenever the
            rep has at least one completed task and isn't already on the
            TaskCompletionPrompt screen. James's diag #25 showed that reps
            confuse "Finish Task" with "Finish Visit" — this nudge tells
            them the visit is still open until they tap Leave Store. */}
        {store.status === 'in_progress'
          && multiTaskState.completedTasks.length > 0
          && visitPhase !== 'task_complete' && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 shadow-card">
            <div className="flex items-start gap-3">
              <svg className="h-6 w-6 flex-shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="font-semibold text-amber-900">Your visit is still open</p>
                <p className="mt-1 text-sm text-amber-800">
                  Finishing a task doesn&apos;t finish the visit. When you&apos;re done at this store, scroll down and tap
                  {' '}
                  <span className="font-semibold underline">Leave Store</span>
                  {' '}
                  to close out and capture the manager signature.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Completed Tasks Summary (shown during active task or task selection) */}
        {store.status === 'in_progress' && multiTaskState.completedTasks.length > 0 && visitPhase !== 'task_complete' && (
          <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
            <h3 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
              Completed Tasks ({multiTaskState.completedTasks.length})
            </h3>
            <div className="space-y-2">
              {multiTaskState.completedTasks.map((task, idx) => (
                <div
                  key={`completed-${task.task_id}-${idx}`}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 ${
                    task.task_type === 'set' ? 'bg-purple-50' : 'bg-blue-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{task.project_description}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(task.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        {' - '}
                        {new Date(task.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <span className={`text-sm font-medium ${task.task_type === 'set' ? 'text-purple-700' : 'text-blue-700'}`}>
                    {task.duration_minutes}m
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current Task Info (shown during active task) */}
        {store.status === 'in_progress' && visitPhase === 'task_active' && multiTaskState.currentTask && (
          <div id="current-task-card" className="mb-4 rounded-xl bg-gradient-to-br from-primary-50 to-primary-100 p-4 shadow-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-primary-700">
                Current Task
              </h3>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                multiTaskState.currentTask.task_type === 'set'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {multiTaskState.currentTask.task_type === 'set' ? 'Set' : 'Call'}
              </span>
            </div>
            <p className="font-semibold text-slate-800">
              <span className="font-mono text-primary-600">#{multiTaskState.currentTask.project_id}</span>{' '}
              {projectDisplayName(multiTaskState.currentTask.project_id, multiTaskState.currentTask.project_description)}
            </p>
            {multiTaskState.currentTask.client_name && (
              <p className="text-sm text-slate-600">{multiTaskState.currentTask.client_name}</p>
            )}
            {multiTaskState.currentTaskStartTime && (
              <p className="mt-2 text-xs text-primary-600">
                Started: {multiTaskState.currentTaskStartTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </p>
            )}
            {(() => {
              const remaining = getRemainingTaskCount();
              return remaining > 0 ? (
                <p className="mt-1 text-xs text-amber-600">
                  +{remaining} other task{remaining !== 1 ? 's' : ''} at this store
                </p>
              ) : null;
            })()}
          </div>
        )}

        {/* Documents & Photos Upload + Survey + Order Entry — only during an
            ACTIVE task. After the task is finished (visitPhase 'task_complete')
            or while selecting another task ('task_select'), the survey/photo
            UI is keyed to the cleared currentTask so it renders blank/reset
            and confuses the rep into thinking their work was lost. #1849
            follow-up. */}
        {store.status === 'in_progress' && visitPhase === 'task_active' && (() => {
          const effectiveTaskType = multiTaskState.currentTask?.task_type || store.taskTypeFromNav;
          const isSetTask = effectiveTaskType === 'set';

          if (isSetTask) {
            const setDeliverables = (
              <>
              {/* Set tasks: Photos, then Documents, then Survey (survey last).
                  Reps want media/paperwork at the top; neither docs nor photos
                  block the checkout flow. (#1471 follow-up #53; order per RMR) */}
              <div className="mb-4">
                <PhotoUpload
                  uploads={photoUploads}
                  onUpload={handlePhotoUpload}
                  onRemove={handlePhotoRemove}
                  disabled={false}
                  maxUploads={10}
                  title="Photos"
                  description="Take photos of shelf displays and store conditions"
                  recoverBlobUrl={recoverFileBlobUrl}
                />
              </div>

              <div className="mb-4">
                <DocumentUpload
                  uploads={documentUploads}
                  onUpload={handleDocumentUpload}
                  onRemove={handleDocumentRemove}
                  disabled={false}
                  maxUploads={5}
                  required={false}
                  title="Documents"
                  description="Upload set completion forms and paperwork (optional)"
                  recoverBlobUrl={recoverFileBlobUrl}
                />
              </div>

              {/* Survey Questions (last) */}
              <div className="mb-4" ref={surveyRef}>
                {renderSurveySection()}
              </div>
              </>
            );
            // #2951 — a non-lead on a team set doesn't have to do these; fold them
            // away (collapsed) but keep them REACHABLE so a teammate can still finish
            // the work if the lead's phone dies mid-set.
            return deliverablesRequired ? (
              setDeliverables
            ) : (
              <details className="mb-4 overflow-hidden rounded-2xl bg-white shadow-card">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                  <span>
                    <span className="block text-sm font-medium text-slate-700">Survey &amp; uploads — team lead only</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Only the team lead needs to complete these. Tap to open if you&apos;re finishing the set for the team.
                    </span>
                  </span>
                  <svg className="h-5 w-5 shrink-0 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="border-t border-slate-100 p-4">{setDeliverables}</div>
              </details>
            );
          }
          return (
            <>
              {/* Call tasks: Photos & Documents (collapsible, optional) ABOVE the
                  Survey, which stays always-visible at the bottom. (order per RMR) */}
              <div className="mb-4">
                <button
                  onClick={() => setShowOptionalUploads(!showOptionalUploads)}
                  className="flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-card text-left"
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                      Photos & Documents
                    </h3>
                    {/* #2077 — mandatory indicator when the project requires attachments. */}
                    {requiredAttachments.requiresPhotos || requiredAttachments.requiresDocuments ? (
                      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                        {[requiredAttachments.requiresPhotos && 'Photos', requiredAttachments.requiresDocuments && 'Documents']
                          .filter(Boolean)
                          .join(' + ')}{' '}
                        required
                      </span>
                    ) : (
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                        Optional
                      </span>
                    )}
                    {(documentUploads.length > 0 || photoUploads.length > 0) && (
                      <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                        {photoUploads.length + documentUploads.length} uploaded
                      </span>
                    )}
                  </div>
                  <svg
                    className={`h-5 w-5 text-slate-400 transition-transform ${showOptionalUploads ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showOptionalUploads && (
                  <div className="mt-2 space-y-4">
                    <PhotoUpload
                      uploads={photoUploads}
                      onUpload={handlePhotoUpload}
                      onRemove={handlePhotoRemove}
                      disabled={false}
                      maxUploads={10}
                      title={requiredAttachments.requiresPhotos ? 'Photos (Required)' : 'Photos'}
                      description={
                        requiredAttachments.requiresPhotos
                          ? 'At least one photo is required to finish this task.'
                          : 'Take photos of shelf displays and store conditions (optional)'
                      }
                      recoverBlobUrl={recoverFileBlobUrl}
                    />
                    <DocumentUpload
                      uploads={documentUploads}
                      onUpload={handleDocumentUpload}
                      onRemove={handleDocumentRemove}
                      disabled={false}
                      maxUploads={5}
                      required={requiredAttachments.requiresDocuments}
                      title={requiredAttachments.requiresDocuments ? 'Documents (Required)' : 'Documents'}
                      description={
                        requiredAttachments.requiresDocuments
                          ? 'At least one document is required to finish this task.'
                          : 'Upload any paperwork or documentation (optional)'
                      }
                      recoverBlobUrl={recoverFileBlobUrl}
                    />
                  </div>
                )}
              </div>

              {/* Survey Questions (last) */}
              <div className="mb-4">
                {renderSurveySection()}
              </div>
            </>
          );
        })()}

        {/* Product capture — shown during an active task when the project requires
            an order and/or an inventory count (#2022). The single button adapts:
            "Scan / Enter Products" (order or both) vs "Take Inventory" (inventory
            only). Hidden for OOS-only / unconfigured-without-order. The capture
            screen renders only the required per-product fields. */}
        {store.status === 'in_progress' && visitPhase === 'task_active' && (() => {
          const steps = workflowStepsForTask(multiTaskState.currentTask);
          if (!steps.order && !steps.inventory && !steps.oos) return null;

          // One button per thing the project actually asks for, named for the job
          // rather than the mechanism (#3515). A single adaptive button had to pick
          // a label, and order won — so on the six projects that require an order
          // AND out-of-stocks the screen said "Scan / Enter Products" and opened in
          // ordering, leaving outs behind a toggle one screen in. That is what Beth
          // reported as an OOS project prompting for an order.
          //
          // The set stays driven by the project: a button for capture the project
          // has turned off is the #2022 defect (the order button showing on a
          // non-order project), so nothing is offered that the project disabled.
          const openCapture = (mode: 'order' | 'inventory' | 'oos') => {
            const projectId = multiTaskState.currentTask?.project_id || store.projectIdFromNav || store.projects[0]?.id || 0;
            const distributorId = multiTaskState.currentTask?.distributor_id || store.distributorIdFromNav || 0;
            // The distributor order rules (min/max). Prefer currentTask (the
            // reconcile persists them there, so they're correct even offline);
            // fall back to the live pending-tasks payload. order_increment is
            // per-product (product cache).
            const matched = allAvailableTasks.find((t) => t.project_id === projectId);
            const minOrder = multiTaskState.currentTask?.min_order_amount ?? matched?.min_order_amount ?? null;
            const maxProduct = multiTaskState.currentTask?.max_product_count ?? matched?.max_product_count ?? null;
            const params = new URLSearchParams({
              // Use legacyStoreId for products API - it requires disambiguation via projectId
              storeId: store.legacyStoreId.toString(),
              storeName: store.name,
              projectId: projectId.toString(),
              distributorId: distributorId.toString(),
              callId: (multiTaskState.currentTaskCallLogId || store.callLogId)?.toString() || '',
              // Visit localId — the same id every other in-visit write keys on.
              // Lets the order's queue op resolve the server callid lazily when
              // the check-in hasn't synced yet (offline-first, #2013).
              visitId: getCurrentTaskLocalId() || store.visitLocalIdFromNav || '',
              // #2022 — which fields the capture screen shows (order/inventory/OOS).
              requiresOrder: steps.order ? '1' : '0',
              requiresInventory: steps.inventory ? '1' : '0',
              requiresOos: steps.oos ? '1' : '0',
              // #3515 — which one the rep asked for. The capture screen opens in
              // this mode instead of inferring one, so it can never start in
              // ordering on a visit the rep came to record outs for.
              mode,
            });
            if (minOrder != null) params.set('minOrder', String(minOrder));
            if (maxProduct != null) params.set('maxProduct', String(maxProduct));
            navigate(`/order?${params.toString()}`);
          };

          const captureButtons: Array<{ mode: 'order' | 'inventory' | 'oos'; label: string; classes: string; testid: string }> = [];
          if (steps.order) {
            captureButtons.push({
              mode: 'order',
              label: 'Order',
              testid: 'capture-order-button',
              classes: 'bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800',
            });
          }
          if (steps.inventory) {
            captureButtons.push({
              mode: 'inventory',
              label: 'Inventory',
              testid: 'capture-inventory-button',
              classes: 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800',
            });
          }
          if (steps.oos) {
            captureButtons.push({
              mode: 'oos',
              label: 'Out of Stock',
              testid: 'capture-oos-button',
              classes: 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800',
            });
          }

          return (
          <div className="mb-4 flex flex-col gap-2">
            {captureButtons.map((b) => (
              <button
                key={b.mode}
                // Kept so anything still keying on the old single button keeps
                // working; the first offered capture is the one it resolves to.
                data-testid={b.mode === captureButtons[0].mode ? 'order-entry-button' : undefined}
                data-capture={b.testid}
                onClick={() => openCapture(b.mode)}
                className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white shadow-lg transition-all ${b.classes}`}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                {b.label}
              </button>
            ))}
          </div>
          );
        })()}

        {/* Start Day Flow - inline when user hasn't started their day */}
        {/* Skip for manual/retroactive entries (skipGpsCheck) since they're backfill work */}
        {store.status === 'pending' && dayData.status !== 'active' && !showStartDayFlow && !skipGpsCheck && (
          <div className="rounded-xl bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-amber-800">You're not clocked in</h4>
                <p className="mt-1 text-sm text-amber-700">
                  Checking in below will clock you in from here. Drove from home? Add that drive first so the
                  time and mileage count.
                </p>
                <button
                  onClick={handleStartDayClick}
                  disabled={isDayLoading}
                  className="mt-3 flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-70"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Add my drive from home
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Location Permission Prompt - part of Start Day flow */}
        {store.status === 'pending' && dayData.status !== 'active' && showLocationPrompt && (
          <div className="mb-4">
            <LocationPermission
              onLocationReady={handleLocationReady}
              onCancel={handleCancelStartDay}
            />
          </div>
        )}

        {/* Retroactive Start Day — when the rep forgot to start their day at
            home. This asks the only three things nothing else can supply: when
            they left, the odometer there, and the odometer here. Until #3519 it
            waited behind a Start Day tap, so a rep who arrived and tapped
            check-in never saw it: 27 clock-ins on prod, every one of them
            retroactive:false, and not one drive with an end odometer. It is now
            what a check-in without an open clock leads to. */}
        {offerOnArrival === 'start_day_backfill' && (
          <div className="mb-4">
            <RetroactiveStartDay
              storeName={store.name}
              onSubmit={handleRetroactiveStartDay}
              onCancel={handleCancelStartDay}
              onSkip={handleSkipOdometer}
              isLoading={isOdometerLoading || isDayLoading}
            />
          </div>
        )}

        {/* Odometer Entry Modal */}
        {showOdometerEntry && store.status === 'pending' && (
          <div className="mb-4">
            <OdometerEntry
              type="store_arrival"
              storeName={store.name}
              calculatedReading={calculatedOdometer}
              lastReading={odometerData.currentReading}
              onSubmit={handleOdometerSubmit}
              onCancel={() => setShowOdometerEntry(false)}
              isLoading={isOdometerLoading || isCheckingIn}
            />
          </div>
        )}

        {/* GPS Check-In - Always show except for manual/retroactive entries (skipGpsCheck=true) */}
        {/* GPSCheckIn handles missing store coordinates by prompting for override */}
        {/* Hide during check-in to prevent loop when odometer entry closes */}
        {offerOnArrival === 'gps_check_in' && (
          <GPSCheckIn
            storeLat={store.lat}
            storeLng={store.lng}
            storeName={store.name}
            maxDistanceMeters={200}
            onCheckIn={handleGPSCheckInComplete}
            isLoading={isCheckingIn || isDayLoading}
          />
        )}

        {/* Loading state for full manual entries - show while auto-creating call log */}
        {store.status === 'pending' && isFullManualEntry && (isCreatingManualEntry || !manualEntryCreated) && (
          <div className="flex w-full items-center justify-center gap-3 rounded-xl bg-gray-400 py-4 font-semibold text-white">
            <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>Creating Entry...</span>
          </div>
        )}

        {/* Direct Check-In button - ONLY shown for manual/retroactive entries (skipGpsCheck=true) */}
        {/* Hide during check-in to prevent loop when odometer entry closes */}
        {/* For full manual entries, the auto-create effect creates the call log - don't show button */}
        {/* No active day requirement for manual entries - they're backfill/out-of-cycle work */}
        {store.status === 'pending' && skipGpsCheck && !showOdometerEntry && !isCheckingIn && !isFullManualEntry && (
          <button
            onClick={() => handleGPSCheckInComplete()}
            disabled={isDayLoading}
            className="flex w-full items-center justify-center gap-3 rounded-xl bg-green-600 py-4 font-semibold text-white shadow-lg transition-all hover:bg-green-700 active:scale-[0.98] disabled:opacity-70"
          >
            {isCheckingIn || isDayLoading ? (
              <>
                <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Checking In...</span>
              </>
            ) : (
              <>
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Check In</span>
              </>
            )}
          </button>
        )}

        {/* Finish Task Button + Pause Option - shown during active task */}
        {store.status === 'in_progress' && visitPhase === 'task_active' && multiTaskState.currentTask && (
          <div className="space-y-3">
            {taskError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {taskError}
              </div>
            )}
            <button
              onClick={handleFinishTask}
              disabled={isFinishing}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-green-600 py-4 font-semibold text-white shadow-lg transition-all hover:bg-green-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-green-600"
            >
              {isFinishing ? (
                <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {/* Manual mode (#1863): show "Confirm Task End (HH:MM)" so the
                  rep sees exactly which pre-entered time will be recorded.
                  Live mode keeps the imperative "Finish This Task" label.
                  In-flight (#1946): show "Saving..." regardless of mode so
                  the rep knows the tap registered. */}
              <span>
                {isFinishing
                  ? 'Saving...'
                  : clock.isManual
                    ? `Confirm Task End (${clock.toHHMM(clock.taskEnd())})`
                    : 'Finish This Task'}
              </span>
            </button>
            <button
              onClick={() => setShowPauseVisitModal(true)}
              disabled={isFinishing}
              className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-amber-500 bg-amber-50 py-3 font-semibold text-amber-700 transition-all hover:bg-amber-100 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-amber-50"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Pause / Switch Task</span>
            </button>
            <button
              onClick={() => setShowAbandonConfirm(true)}
              className="w-full py-2 text-center text-sm text-slate-400 transition-colors hover:text-red-500"
            >
              Abandon task
            </button>
          </div>
        )}

        {/* Abandon Task Confirmation */}
        {showAbandonConfirm && multiTaskState.currentTask && multiTaskState.currentTaskStartTime && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-bold text-slate-900">Abandon Task?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Discard {Math.max(1, Math.round((Date.now() - multiTaskState.currentTaskStartTime.getTime()) / 60000))} minutes of work on{' '}
                <span className="font-medium">{multiTaskState.currentTask.project_description}</span>?
                This cannot be undone.
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => setShowAbandonConfirm(false)}
                  className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Keep Working
                </button>
                <button
                  onClick={handleAbandonTask}
                  className="flex-1 rounded-lg bg-red-600 py-2.5 font-medium text-white transition-colors hover:bg-red-700"
                >
                  Abandon
                </button>
              </div>
            </div>
          </div>
        )}

        {discardTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-bold text-slate-900">Discard paused session?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Remove the paused session for <span className="font-medium">{discardTarget.name}</span>. Its
                time won&apos;t be recorded and it won&apos;t appear at checkout. This cannot be undone.
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => setDiscardTarget(null)}
                  className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Keep It
                </button>
                <button
                  onClick={confirmDiscardPausedTask}
                  className="flex-1 rounded-lg bg-red-600 py-2.5 font-medium text-white transition-colors hover:bg-red-700"
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Tasks Preview - shown during active task so user can see what's coming */}
        {store.status === 'in_progress' && visitPhase === 'task_active' && multiTaskState.currentTask && (
          isLoadingTasks ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                Loading other tasks...
              </div>
            </div>
          ) : (
            <PendingTasksPreview
              availableTasks={availableTasks}
              currentTaskId={multiTaskState.currentTask.task_id}
              completedTaskIds={getCompletedTaskIds()}
            />
          )
        )}

        {/* Leave Store / Pause Visit Buttons - shown when in task selection phase OR when working without a specific task */}
        {store.status === 'in_progress' && (visitPhase === 'task_select' || (visitPhase === 'task_active' && !multiTaskState.currentTask)) && (
          <div className="space-y-3">
            <button
              onClick={handleLeaveStore}
              disabled={isCheckingOut || hasUnfinishedTouchedTasks}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-primary-600 py-4 font-semibold text-white shadow-lg transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-70 disabled:hover:bg-primary-600"
            >
              {isCheckingOut ? (
                <>
                  <svg className="h-6 w-6 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {/* Manual mode (#1863): show pre-entered time-out so the rep
                      confirms which timestamp will be recorded. */}
                  <span>
                    {clock.isManual ? `Confirm Departure (${clock.toHHMM(clock.departure())})` : 'Leave Store'}
                  </span>
                </>
              )}
            </button>
            {hasUnfinishedTouchedTasks && (
              <p className="text-center text-sm text-amber-700">
                Finish or pause your current task before leaving:{' '}
                <span className="font-medium">
                  {unfinishedTouchedTasks.map((t) => t.project_description).join(', ')}
                </span>
              </p>
            )}
            <button
              onClick={() => setShowPauseVisitModal(true)}
              disabled={isCheckingOut}
              className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-amber-500 bg-amber-50 py-3 font-semibold text-amber-700 transition-all hover:bg-amber-100 active:scale-[0.98] disabled:opacity-70"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Pause / Switch Task</span>
            </button>
          </div>
        )}

        {store.status === 'completed' && (
          <div className="rounded-xl bg-green-50 p-4 text-center">
            <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-green-800">Visit Completed</h3>
            <p className="mt-1 text-sm text-green-600">
              {formatTimeForDisplay(store.checkInTime)}
              {' - '}
              {formatTimeForDisplay(store.checkOutTime)}
            </p>
            {/* Edit button - opens ManualCallEntry modal with all data pre-filled */}
            {editingCallLog && (
              <button
                onClick={() => setShowEditVisitModal(true)}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit Visit
              </button>
            )}
          </div>
        )}
      </main>

      {/* Map Directions Modal */}
      {showMapModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <div className="w-full max-w-lg slide-up">
            <MapDirections
              storeName={store.name}
              storeAddress={`${store.address}, ${store.city}`}
              lat={store.lat}
              lng={store.lng}
              onClose={() => setShowMapModal(false)}
            />
          </div>
        </div>
      )}

      {/* Checkout Summary Modal */}
      <CheckoutSummary
        isOpen={showCheckoutSummary}
        storeId={store.id}
        storeName={store.name}
        requiresSignature={requiresSignature}
        projects={
          // Multi-task visit: completed tasks (status complete) PLUS any paused
          // tasks the rep is leaving unfinished — shown as incomplete so they
          // understand that work ships with their hours but isn't finished.
          // Otherwise fall back to the legacy single-project shape.
          multiTaskState.completedTasks.length > 0 || multiTaskState.pausedTasks.length > 0
            ? [
                ...completedTasksToProjectSummaries(multiTaskState.completedTasks),
                ...multiTaskState.pausedTasks.map((p): ProjectSummary => ({
                  projectId: p.task.project_id,
                  projectType: p.task.task_type,
                  projectName: p.task.project_description,
                  clientName: p.task.client_name || undefined,
                  timeMinutes: p.accumulatedMinutes,
                  status: 'incomplete' as const,
                })),
              ]
            : store.projects.map((p): ProjectSummary => ({
                projectId: p.id,
                projectType: 'call',
                projectName: p.name,
                timeMinutes: store.checkInTime && parseTimeToDate(store.checkInTime)
                  ? Math.max(0, Math.floor((Date.now() - parseTimeToDate(store.checkInTime)!.getTime()) / 60000))
                  : 0,
                status: p.completed ? 'complete' : 'incomplete',
              }))
        }
        totalTimeMinutes={
          // Use multi-task total time if available, otherwise calculate from check-in
          // For full manual entries, calculate from pre-entered times
          isFullManualEntry && manualTimeIn && manualTimeOut
            ? (() => {
                const [inH, inM] = manualTimeIn.split(':').map(Number);
                const [outH, outM] = manualTimeOut.split(':').map(Number);
                let diff = (outH * 60 + outM) - (inH * 60 + inM);
                if (diff < 0) diff += 24 * 60; // Handle overnight
                return diff;
              })()
            : multiTaskState.completedTasks.length > 0
              ? getTotalTimeMinutes()
              : store.checkInTime && parseTimeToDate(store.checkInTime)
                ? Math.max(0, Math.floor((Date.now() - parseTimeToDate(store.checkInTime)!.getTime()) / 60000))
                : 0
        }
        // Backfill entries have no live odometer to confirm.
        lastOdometer={isManualEntry ? null : odometerData.currentReading}
        onComplete={handleCheckoutComplete}
        onCancel={() => setShowCheckoutSummary(false)}
        orderReview={orderReview ?? undefined}
        // #2959 — team info is authoritative from the SET assignment when this
        // visit maps to a team set: pass it locked (read-only indicator, not a
        // toggle). Fall back to the manual-entry pre-fill otherwise.
        initialTeamProject={
          checkoutTeamConfig ? true : isFullManualEntry ? manualTeamProject : undefined
        }
        initialTeamLeader={
          checkoutTeamConfig
            ? checkoutTeamConfig.isCurrentUserLead
            : isFullManualEntry
              ? manualTeamLeader
              : undefined
        }
        initialNotes={isFullManualEntry ? manualNotes : undefined}
        // Store times for display and editing. Fallback chain (#1957
        // follow-up — RMR diag #29 reported the check-in field was empty
        // on the summary modal even when the rep had finished tasks):
        //   1. Manual entry's explicit timeIn (full manual flow)
        //   2. store.checkInTime         (server CallLog earliest stcalltime)
        //   3. multiTaskState.arrivalTime (local IDB arrival)
        //   4. EARLIEST completed task's start_time
        //   5. Current task's start time (rep is mid-task)
        // Any of (4) or (5) is concrete data the rep just witnessed; we
        // should not surface an empty field when we have a real value
        // sitting one fallback step away.
        storeCheckInTime={(() => {
          if (isFullManualEntry && manualTimeIn && manualDate) {
            return `${manualDate}T${manualTimeIn}:00`;
          }
          // Trust the server check-in ONLY when it's a full datetime. A
          // calllog list read can hand back a TIME-ONLY string (the
          // serializer's str() fallback), which can't be edited in the time
          // picker and skews the duration — so fall through to the clean local
          // arrival timestamp instead of surfacing a blank/negative summary. (#2017)
          if (store.checkInTime && isFullDateTime(store.checkInTime)) return store.checkInTime;
          if (multiTaskState.arrivalTime) return multiTaskState.arrivalTime.toISOString();
          if (multiTaskState.completedTasks.length > 0) {
            return multiTaskState.completedTasks
              .map((t) => t.start_time)
              .sort()[0];
          }
          if (multiTaskState.currentTaskStartTime) {
            return multiTaskState.currentTaskStartTime.toISOString();
          }
          // Last resort: a time-only server value is still better than blank.
          if (store.checkInTime) return store.checkInTime;
          return undefined;
        })()}
        storeCheckOutTime={
          isFullManualEntry && manualTimeOut && manualDate
            ? `${manualDate}T${manualTimeOut}:00`
            : undefined  // Will default to now in component
        }
        // #2104 feature gate: editable checkout time + Split Time control.
        splitTimeEnabled={splitTimeEnabled}
      />

      {/* Pause Visit Confirmation Modal */}
      {showPauseVisitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Pause This Task?</h3>
                <p className="text-sm text-slate-500">Stop the timer but stay checked in</p>
              </div>
            </div>

            <div className="mb-4 rounded-lg bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                The task timer stops and this work session is recorded.
                You'll stay checked in at the store — switch to a different task
                or resume any paused task when you're ready.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowPauseVisitModal(false)}
                className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePauseVisit}
                className="flex-1 rounded-lg bg-amber-600 py-2.5 font-medium text-white transition-colors hover:bg-amber-700"
              >
                Pause Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Visit Modal */}
      {showCancelVisitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900">Cancel Visit?</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will void the check-in and discard all time recorded at this store.
            </p>
            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">Reason</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Select a reason...</option>
                <option value="Wrong store">Wrong store</option>
                <option value="Store closed">Store closed</option>
                <option value="Testing/admin">Testing/admin</option>
                <option value="Other">Other</option>
              </select>
              {cancelReason === 'Other' && (
                <input
                  type="text"
                  value={cancelReasonOther}
                  onChange={(e) => setCancelReasonOther(e.target.value)}
                  placeholder="Describe the reason..."
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              )}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  setShowCancelVisitModal(false);
                  setCancelReason('');
                  setCancelReasonOther('');
                }}
                className="flex-1 rounded-lg border border-slate-200 py-2.5 font-medium text-slate-700 transition-colors hover:bg-slate-50"
                disabled={isCancelling}
              >
                Go Back
              </button>
              <button
                onClick={handleCancelVisit}
                disabled={!cancelReason || (cancelReason === 'Other' && !cancelReasonOther.trim()) || isCancelling}
                className="flex-1 rounded-lg bg-red-600 py-2.5 font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {isCancelling ? 'Cancelling...' : 'Cancel Visit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Visit Modal - ManualCallEntry handles all edit logic including signature checks */}
      <ManualCallEntry
        isOpen={showEditVisitModal}
        onClose={() => setShowEditVisitModal(false)}
        editingCallLog={editingCallLog}
        onSaveComplete={() => {
          // Refresh the store data after edit
          setShowEditVisitModal(false);
          // Trigger a refetch by clearing and reloading
          setIsLoading(true);
          setStore(null);
        }}
      />

      {/* GPS verification reason modal for manual entries (#1779) */}
      <GpsVerificationReasonModal
        isOpen={showVerificationModal}
        reasonCode={pendingManualEntry?.code ?? null}
        distance={pendingManualEntry?.distance ?? null}
        storeName={store?.name ?? 'this store'}
        onCancel={handleVerificationCancel}
        onSubmit={handleVerificationSubmit}
      />
    </div>
  );
}
